import { makeApi, Zodios, type ZodiosOptions } from '@zodios/core';
import { z } from 'zod';

type Company = Partial<{
  id: string;
  version: number;
  kind: string;
  entityType: string;
  entityStatus: string;
  changedByAdmin: boolean;
  changedByAdminUser: string;
  changedByCustomer: boolean;
  createdDate: string;
  createdBy: string;
  lastUpdatedDate: string;
  lastUpdatedBy: string;
  email: string;
  userCustomerRole: string;
  capabilities: Array<string>;
  customerName: string;
  companyName: string;
  companyNumber: string;
  businessNumber: string;
  companyExtractFileId: UUID;
  registeredOffice: Address;
  principalPlaceOfBusiness: Address;
  listedExchange: string;
  ticker: string;
  ultimateBeneficialOwners: Array<PersonReference>;
  directors: Array<PersonReference>;
  wholesaleCertificateHolder: PersonReference;
  wholesaleCertificate: WholesaleCertificate;
  fileId: UUID;
}>;
type UUID = string;
type Address = Partial<{
  addressType: string;
  addressStatus: string;
  unitNumber: string;
  streetNumber: string;
  streetName: string;
  streetType: string;
  suburb: string;
  postcode: string;
  city: string;
  state: string;
  countryCode: string;
}>;
type Person = Partial<{
  id: string;
  version: number;
  kind: string;
  entityType: string;
  entityStatus: string;
  changedByAdmin: boolean;
  changedByAdminUser: string;
  changedByCustomer: boolean;
  createdDate: string;
  createdBy: string;
  lastUpdatedDate: string;
  lastUpdatedBy: string;
  email: string;
  userCustomerRole: string;
  capabilities: Array<string>;
  customerName: string;
  firstName: string;
  middleName: string;
  familyName: string;
  gender: string;
  dateOfBirth: string;
  address: Address;
  driverLicence: DriverLicence;
  medicareCard: MedicareCard;
  otherIdentityDocument: OtherIdentityDocument;
  passport: Passport;
  photoCard: PhotoCard;
  immiCard: ImmiCard;
  preferredContactNumber: string;
  wholesaleCertificate: WholesaleCertificate;
}>;
type DriverLicence = Partial<{
  captureStatus: IdentityDocumentCaptureStatus;
  licenceStatus: string;
  licenceCountryOfIssue: string;
  licenceState: string;
  licenceNumber: string;
  licenceCardNumber: string;
  licenceVersionNumber: string;
  licenceName: string;
  licenceFirstName: string;
  licenceMiddleName: string;
  licenceFamilyName: string;
  licenceExpiry: string;
}>;
type IdentityDocumentCaptureStatus = 'Incomplete' | 'Passed' | 'Failed';
type MedicareCard = Partial<{
  captureStatus: IdentityDocumentCaptureStatus;
  medicareStatus: string;
  medicareValidationMethod: string;
  medicareCardNumber: string;
  medicareCardColour: string;
  medicareCardExpiry: string;
  medicareCardIndividualRefNumber: number;
  medicareCardFullName: string;
}>;
type OtherIdentityDocument = Partial<{
  captureStatus: IdentityDocumentCaptureStatus;
  otherDocumentType: string;
  documentStatus: string;
  documentIdentifier: string;
  countryOfIssue: string;
  dateOfIssue: string;
  dateOfExpiry: string;
}>;
type Passport = Partial<{
  captureStatus: IdentityDocumentCaptureStatus;
  passportStatus: string;
  passportNumber: string;
  passportName: string;
  passportGivenNames: string;
  passportFamilyName: string;
  passportExpiry: string;
  passportCountryOfIssue: string;
}>;
type PhotoCard = {
  captureStatus?: IdentityDocumentCaptureStatus | undefined;
  photoCardStatus?: string | undefined;
  photoCardCountryOfIssue?: string | undefined;
  photoCardState: string;
  photoCardNumber?: string | undefined;
  photoCardName?: string | undefined;
  photoCardFirstName?: string | undefined;
  photoCardMiddleName?: string | undefined;
  photoCardFamilyName?: string | undefined;
  photoCardExpiry?: string | undefined;
};
type ImmiCard = Partial<{
  captureStatus: IdentityDocumentCaptureStatus;
  immiCardStatus: string;
  immiCardNumber: string;
  immiCardName: string;
  immiCardGivenNames: string;
  immiCardFamilyName: string;
  immiCardExpiry: string;
}>;
type WholesaleCertificate = Partial<{
  id: UUID;
  version: number;
  fileId: UUID;
  supportDocumentSubType: SupportDocumentSubType;
  status: EntityStatus;
  createdDate: OffsetDateTime;
  createdBy: string;
  lastUpdatedDate: OffsetDateTime;
  lastUpdatedBy: string;
  customerId: UUID;
  keyAttributeName: string;
  documentType: 'RealEstateSupportDocument' | 'OtherSupportDocument' | 'WholesaleCertificate';
  dateIssued: LocalDate;
  dateOfExpiry: LocalDate;
  accountantNumber: string;
  certificateStatus: EntityStatus;
}>;
type SupportDocumentSubType =
  | 'CertificateOfTitle'
  | 'NationalVendorDeclarationForLivestock'
  | 'Other'
  | 'RatesNotice'
  | 'ValuationNotice'
  | 'WholesaleCertificate';
type EntityStatus = 'Draft' | 'InProgress' | 'Rejected' | 'Verified';
type OffsetDateTime = string;
type LocalDate = string;
type TrustType =
  | 'FamilyDiscretionaryTrust'
  | 'CharitableTrust'
  | 'TestamentaryTrust'
  | 'UnitTrust'
  | 'HybridTrust'
  | 'ForeignTrust'
  | 'DeceasedEstateTrust'
  | 'OtherType'
  | 'RegisteredManagedInvestmentScheme'
  | 'UnregisteredManagedInvestmentScheme'
  | 'RegulatedUnderACommonwealthStatutoryRegulator'
  | 'GovernmentSuperannuationFund';
type RedactedCustomer = Partial<{
  kind: string;
  id: string;
  version: number;
  entityType: string;
  entityStatus: string;
  changedByAdmin: boolean;
  changedByAdminUser: string;
  changedByCustomer: boolean;
  createdDate: string;
  createdBy: string;
  lastUpdatedDate: string;
  lastUpdatedBy: string;
  email: string;
  userCustomerRole: string;
  capabilities: Array<string>;
  customerName: string;
  name: string;
  redactedAt: OffsetDateTime;
  redactedBy: string;
  redactionReason: string;
}>;
type Customer = Person | Company | Trust | SoleTrader | Partnership | RedactedCustomer;
type CustomerReference = Partial<{
  id: string;
  customer: Customer;
}>;
type MetaAttribute = Partial<{
  name: string;
  dataType: string;
  label: string;
  description: string;
  accessLevel: AccessLevel;
  createOnly: boolean;
  readOnly: boolean;
  readRoles: Array<string>;
  writeRoles: Array<string>;
  ruleList: Array<MetaRule>;
}>;
type AccessLevel = 'RestrictedToCustomer' | 'RestrictedToClient' | 'Unrestricted';
type MetaEntity = Partial<{
  entityType: string;
  attributeList: Array<MetaAttribute>;
  ruleList: Array<MetaRule>;
}>;
type MetaRule = Partial<{
  metaEntity: MetaEntity;
  metaAttribute: MetaAttribute;
  ruleType: string;
}>;
type Organisation = Partial<{
  id: string;
  version: number;
  organisationType: 'Client';
  entityId: string;
  organisationStatus: 'Active' | 'Inactive';
  organisationName: string;
  children: Array<Organisation>;
}>;
type Partnership = Partial<{
  id: string;
  version: number;
  kind: string;
  entityType: string;
  entityStatus: string;
  changedByAdmin: boolean;
  changedByAdminUser: string;
  changedByCustomer: boolean;
  createdDate: string;
  createdBy: string;
  lastUpdatedDate: string;
  lastUpdatedBy: string;
  email: string;
  userCustomerRole: string;
  capabilities: Array<string>;
  customerName: string;
  partnershipName: string;
  businessNumber: string;
  principalPlaceOfBusiness: Address;
  partnershipAgreementFileId: UUID;
  partners: Array<CustomerReference>;
  wholesaleCertificateHolder: PersonReference;
  wholesaleCertificate: WholesaleCertificate;
}>;
type PersonReference = Partial<{
  id: string;
  customer: Customer;
}>;
type SoleTrader = Partial<{
  id: string;
  version: number;
  kind: string;
  entityType: string;
  entityStatus: string;
  changedByAdmin: boolean;
  changedByAdminUser: string;
  changedByCustomer: boolean;
  createdDate: string;
  createdBy: string;
  lastUpdatedDate: string;
  lastUpdatedBy: string;
  email: string;
  userCustomerRole: string;
  capabilities: Array<string>;
  customerName: string;
  soleTraderName: string;
  businessNumber: string;
  principalPlaceOfBusiness: Address;
  individual: PersonReference;
  wholesaleCertificateHolder: PersonReference;
  wholesaleCertificate: WholesaleCertificate;
}>;
type Trust = Partial<{
  id: string;
  version: number;
  kind: string;
  entityType: string;
  entityStatus: string;
  changedByAdmin: boolean;
  changedByAdminUser: string;
  changedByCustomer: boolean;
  createdDate: string;
  createdBy: string;
  lastUpdatedDate: string;
  lastUpdatedBy: string;
  email: string;
  userCustomerRole: string;
  capabilities: Array<string>;
  customerName: string;
  address: Address;
  businessNumber: string;
  trustName: string;
  businessNameOfTrustee: string;
  deedFileId: UUID;
  wholesaleCertificateHolder: PersonReference;
  ultimateBeneficialOwners: Array<PersonReference>;
  trustees: Array<CustomerReference>;
  settlors: Array<PersonReference>;
  trustType: TrustType;
  wholesaleCertificate: WholesaleCertificate;
}>;

const ClientRecordAnnulmentRequest = z
  .object({
    reason: z.string(),
    dryRun: z.boolean().optional(),
    expectedPlanSha256: z.string().optional(),
  })
  .passthrough();
const UUID = z.string();
const TablePlan = z
  .object({ table: z.string(), count: z.number().int(), md5: z.string(), ids: z.array(z.string()) })
  .partial()
  .passthrough();
const ControllerRow = z
  .object({ permissionId: z.string(), customerId: z.string(), inPair: z.boolean() })
  .partial()
  .passthrough();
const ForeignOwned = z
  .object({ requests: z.array(z.string()), permissionContexts: z.array(z.string()) })
  .partial()
  .passthrough();
const ReportReference = z
  .object({ riskAssessmentId: z.string(), reportId: z.string() })
  .partial()
  .passthrough();
const SchemaFindings = z
  .object({
    unknownColumns: z.array(z.string()),
    absentTables: z.array(z.string()),
    unknownDependentTables: z.array(z.string()),
    unsafeCascades: z.array(z.string()),
    columnsNotOnTarget: z.record(z.array(z.string())),
  })
  .partial()
  .passthrough();
const AnnulmentPlan = z
  .object({
    customerId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    organisationId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    customerExists: z.boolean(),
    organisationExists: z.boolean(),
    customerRedacted: z.boolean(),
    tables: z.array(TablePlan),
    keptSharedMetadata: z.array(z.string()),
    permissionControllerRows: z.array(ControllerRow),
    foreignOwned: ForeignOwned,
    reports: z.array(ReportReference),
    schema: SchemaFindings,
    planSha256: z.string(),
  })
  .partial()
  .passthrough();
const AnnulmentRefusal = z.enum([
  'CUSTOMER_NOT_FOUND',
  'ORGANISATION_NOT_FOUND',
  'CUSTOMER_REDACTED',
  'PAIR_HAS_NO_ROWS',
  'CONTROLLER_ROW_OUTSIDE_PAIR',
  'FOREIGN_OWNED_ROWS',
  'UNKNOWN_DEPENDENT_TABLE',
  'UNSAFE_CASCADE',
  'UNKNOWN_COLUMN',
  'SCOPE_TABLE_ABSENT',
]);
const ClientRecordAnnulmentResponse = z
  .object({
    annulmentId: UUID,
    status: z.string(),
    plan: AnnulmentPlan,
    refusals: z.array(AnnulmentRefusal),
    archivePrefix: z.string(),
    reportStepStatus: z.string(),
    reportStepDetail: z.string(),
  })
  .partial()
  .passthrough();
const CustomerRedactionRequest = z.object({ reason: z.string() }).passthrough();
const Address = z
  .object({
    addressType: z.string(),
    addressStatus: z.string(),
    unitNumber: z.string(),
    streetNumber: z.string(),
    streetName: z.string(),
    streetType: z.string(),
    suburb: z.string(),
    postcode: z.string(),
    city: z.string(),
    state: z.string(),
    countryCode: z.string(),
  })
  .partial()
  .passthrough();
const IdentityDocumentCaptureStatus = z.enum(['Incomplete', 'Passed', 'Failed']);
const DriverLicence = z
  .object({
    captureStatus: IdentityDocumentCaptureStatus,
    licenceStatus: z.string(),
    licenceCountryOfIssue: z.string(),
    licenceState: z.string(),
    licenceNumber: z.string(),
    licenceCardNumber: z.string(),
    licenceVersionNumber: z.string(),
    licenceName: z.string(),
    licenceFirstName: z.string(),
    licenceMiddleName: z.string(),
    licenceFamilyName: z.string(),
    licenceExpiry: z.string(),
  })
  .partial()
  .passthrough();
const MedicareCard = z
  .object({
    captureStatus: IdentityDocumentCaptureStatus,
    medicareStatus: z.string(),
    medicareValidationMethod: z.string(),
    medicareCardNumber: z.string(),
    medicareCardColour: z.string(),
    medicareCardExpiry: z.string(),
    medicareCardIndividualRefNumber: z.number().int(),
    medicareCardFullName: z.string(),
  })
  .partial()
  .passthrough();
const OtherIdentityDocument = z
  .object({
    captureStatus: IdentityDocumentCaptureStatus,
    otherDocumentType: z.string(),
    documentStatus: z.string(),
    documentIdentifier: z.string(),
    countryOfIssue: z.string(),
    dateOfIssue: z.string(),
    dateOfExpiry: z.string(),
  })
  .partial()
  .passthrough();
const Passport = z
  .object({
    captureStatus: IdentityDocumentCaptureStatus,
    passportStatus: z.string(),
    passportNumber: z.string(),
    passportName: z.string(),
    passportGivenNames: z.string(),
    passportFamilyName: z.string(),
    passportExpiry: z.string(),
    passportCountryOfIssue: z.string(),
  })
  .partial()
  .passthrough();
const PhotoCard = z
  .object({
    captureStatus: IdentityDocumentCaptureStatus.optional(),
    photoCardStatus: z.string().optional(),
    photoCardCountryOfIssue: z.string().optional(),
    photoCardState: z.string(),
    photoCardNumber: z.string().optional(),
    photoCardName: z.string().optional(),
    photoCardFirstName: z.string().optional(),
    photoCardMiddleName: z.string().optional(),
    photoCardFamilyName: z.string().optional(),
    photoCardExpiry: z.string().optional(),
  })
  .passthrough();
const ImmiCard = z
  .object({
    captureStatus: IdentityDocumentCaptureStatus,
    immiCardStatus: z.string(),
    immiCardNumber: z.string(),
    immiCardName: z.string(),
    immiCardGivenNames: z.string(),
    immiCardFamilyName: z.string(),
    immiCardExpiry: z.string(),
  })
  .partial()
  .passthrough();
const SupportDocumentSubType = z.enum([
  'CertificateOfTitle',
  'NationalVendorDeclarationForLivestock',
  'Other',
  'RatesNotice',
  'ValuationNotice',
  'WholesaleCertificate',
]);
const EntityStatus = z.enum(['Draft', 'InProgress', 'Rejected', 'Verified']);
const OffsetDateTime = z.string();
const LocalDate = z.string();
const WholesaleCertificate = z
  .object({
    id: UUID,
    version: z.number().int(),
    fileId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    supportDocumentSubType: SupportDocumentSubType,
    status: EntityStatus,
    createdDate: OffsetDateTime,
    createdBy: z.string(),
    lastUpdatedDate: OffsetDateTime,
    lastUpdatedBy: z.string(),
    customerId: UUID,
    keyAttributeName: z.string(),
    documentType: z.enum([
      'RealEstateSupportDocument',
      'OtherSupportDocument',
      'WholesaleCertificate',
    ]),
    dateIssued: LocalDate,
    dateOfExpiry: LocalDate,
    accountantNumber: z.string(),
    certificateStatus: EntityStatus,
  })
  .partial()
  .passthrough();
const Person = z
  .object({
    id: z.string(),
    version: z.number().int(),
    kind: z.string(),
    entityType: z.string(),
    entityStatus: z.string(),
    changedByAdmin: z.boolean(),
    changedByAdminUser: z.string(),
    changedByCustomer: z.boolean(),
    createdDate: z.string(),
    createdBy: z.string(),
    lastUpdatedDate: z.string(),
    lastUpdatedBy: z.string(),
    email: z.string(),
    userCustomerRole: z.string(),
    capabilities: z.array(z.string()),
    customerName: z.string(),
    firstName: z.string(),
    middleName: z.string(),
    familyName: z.string(),
    gender: z.string(),
    dateOfBirth: z.string(),
    address: Address,
    driverLicence: DriverLicence,
    medicareCard: MedicareCard,
    otherIdentityDocument: OtherIdentityDocument,
    passport: Passport,
    photoCard: PhotoCard,
    immiCard: ImmiCard,
    preferredContactNumber: z.string(),
    wholesaleCertificate: WholesaleCertificate,
  })
  .partial()
  .passthrough();
const PersonReference: z.ZodType<PersonReference> = z.lazy(() =>
  z.object({ id: z.string(), customer: Customer }).partial().passthrough(),
);
const Company: z.ZodType<Company> = z.lazy(() =>
  z
    .object({
      id: z.string(),
      version: z.number().int(),
      kind: z.string(),
      entityType: z.string(),
      entityStatus: z.string(),
      changedByAdmin: z.boolean(),
      changedByAdminUser: z.string(),
      changedByCustomer: z.boolean(),
      createdDate: z.string(),
      createdBy: z.string(),
      lastUpdatedDate: z.string(),
      lastUpdatedBy: z.string(),
      email: z.string(),
      userCustomerRole: z.string(),
      capabilities: z.array(z.string()),
      customerName: z.string(),
      companyName: z.string(),
      companyNumber: z.string(),
      businessNumber: z.string(),
      companyExtractFileId: UUID.regex(
        /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
      ).uuid(),
      registeredOffice: Address,
      principalPlaceOfBusiness: Address,
      listedExchange: z.string(),
      ticker: z.string(),
      ultimateBeneficialOwners: z.array(PersonReference),
      directors: z.array(PersonReference),
      wholesaleCertificateHolder: PersonReference,
      wholesaleCertificate: WholesaleCertificate,
      fileId: UUID,
    })
    .partial()
    .passthrough(),
);
const CustomerReference: z.ZodType<CustomerReference> = z.lazy(() =>
  z.object({ id: z.string(), customer: Customer }).partial().passthrough(),
);
const TrustType = z.enum([
  'FamilyDiscretionaryTrust',
  'CharitableTrust',
  'TestamentaryTrust',
  'UnitTrust',
  'HybridTrust',
  'ForeignTrust',
  'DeceasedEstateTrust',
  'OtherType',
  'RegisteredManagedInvestmentScheme',
  'UnregisteredManagedInvestmentScheme',
  'RegulatedUnderACommonwealthStatutoryRegulator',
  'GovernmentSuperannuationFund',
]);
const Trust: z.ZodType<Trust> = z.lazy(() =>
  z
    .object({
      id: z.string(),
      version: z.number().int(),
      kind: z.string(),
      entityType: z.string(),
      entityStatus: z.string(),
      changedByAdmin: z.boolean(),
      changedByAdminUser: z.string(),
      changedByCustomer: z.boolean(),
      createdDate: z.string(),
      createdBy: z.string(),
      lastUpdatedDate: z.string(),
      lastUpdatedBy: z.string(),
      email: z.string(),
      userCustomerRole: z.string(),
      capabilities: z.array(z.string()),
      customerName: z.string(),
      address: Address,
      businessNumber: z.string(),
      trustName: z.string(),
      businessNameOfTrustee: z.string(),
      deedFileId: UUID.regex(
        /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
      ).uuid(),
      wholesaleCertificateHolder: PersonReference,
      ultimateBeneficialOwners: z.array(PersonReference),
      trustees: z.array(CustomerReference),
      settlors: z.array(PersonReference),
      trustType: TrustType,
      wholesaleCertificate: WholesaleCertificate,
    })
    .partial()
    .passthrough(),
);
const SoleTrader: z.ZodType<SoleTrader> = z.lazy(() =>
  z
    .object({
      id: z.string(),
      version: z.number().int(),
      kind: z.string(),
      entityType: z.string(),
      entityStatus: z.string(),
      changedByAdmin: z.boolean(),
      changedByAdminUser: z.string(),
      changedByCustomer: z.boolean(),
      createdDate: z.string(),
      createdBy: z.string(),
      lastUpdatedDate: z.string(),
      lastUpdatedBy: z.string(),
      email: z.string(),
      userCustomerRole: z.string(),
      capabilities: z.array(z.string()),
      customerName: z.string(),
      soleTraderName: z.string(),
      businessNumber: z.string(),
      principalPlaceOfBusiness: Address,
      individual: PersonReference,
      wholesaleCertificateHolder: PersonReference,
      wholesaleCertificate: WholesaleCertificate,
    })
    .partial()
    .passthrough(),
);
const Partnership: z.ZodType<Partnership> = z.lazy(() =>
  z
    .object({
      id: z.string(),
      version: z.number().int(),
      kind: z.string(),
      entityType: z.string(),
      entityStatus: z.string(),
      changedByAdmin: z.boolean(),
      changedByAdminUser: z.string(),
      changedByCustomer: z.boolean(),
      createdDate: z.string(),
      createdBy: z.string(),
      lastUpdatedDate: z.string(),
      lastUpdatedBy: z.string(),
      email: z.string(),
      userCustomerRole: z.string(),
      capabilities: z.array(z.string()),
      customerName: z.string(),
      partnershipName: z.string(),
      businessNumber: z.string(),
      principalPlaceOfBusiness: Address,
      partnershipAgreementFileId: UUID.regex(
        /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
      ).uuid(),
      partners: z.array(CustomerReference),
      wholesaleCertificateHolder: PersonReference,
      wholesaleCertificate: WholesaleCertificate,
    })
    .partial()
    .passthrough(),
);
const RedactedCustomer = z
  .object({
    kind: z.string(),
    id: z.string(),
    version: z.number().int(),
    entityType: z.string(),
    entityStatus: z.string(),
    changedByAdmin: z.boolean(),
    changedByAdminUser: z.string(),
    changedByCustomer: z.boolean(),
    createdDate: z.string(),
    createdBy: z.string(),
    lastUpdatedDate: z.string(),
    lastUpdatedBy: z.string(),
    email: z.string(),
    userCustomerRole: z.string(),
    capabilities: z.array(z.string()),
    customerName: z.string(),
    name: z.string(),
    redactedAt: OffsetDateTime,
    redactedBy: z.string(),
    redactionReason: z.string(),
  })
  .partial()
  .passthrough();
const Customer: z.ZodType<Customer> = z.lazy(() =>
  z.union([Person, Company, Trust, SoleTrader, Partnership, RedactedCustomer]),
);
const ResultType = z.enum(['Info', 'Warning', 'Error']);
const ResultScope = z.enum(['Field', 'Form']);
const ValidationResult = z
  .object({
    path: z.string(),
    name: z.string(),
    resultType: ResultType,
    resultName: z.string(),
    resultScope: ResultScope,
    message: z.string(),
  })
  .partial()
  .passthrough();
const CustomerRedactionResponse = z
  .object({ customer: Customer, validationResults: z.array(ValidationResult) })
  .partial()
  .passthrough();
const IntegrationRegistrationRequest = z
  .object({ clientId: z.string(), organisationId: UUID, partnerType: z.string() })
  .passthrough();
const IntegrationRegistrationResponse = z
  .object({ userId: UUID, clientId: z.string() })
  .passthrough();
const UserCredentialSetupStatusUpdateRequest = z
  .object({ withCredentials: z.boolean() })
  .partial()
  .passthrough();
const AdverseMedia = z
  .object({
    id: UUID,
    customerId: UUID,
    adverseMediaStatus: z.boolean(),
    lastUpdatedDatetime: OffsetDateTime,
    lastUpdatedBy: z.string(),
    commentText: z.string(),
  })
  .passthrough();
const CreateAdverseMediaRequest = z
  .object({ comment: z.string(), hasAdverseMedia: z.boolean() })
  .passthrough();
const CreateCompanyRequest = z
  .object({
    entityType: z.string(),
    entityStatus: z.string(),
    companyName: z.string(),
    companyNumber: z.string(),
    businessNumber: z.string(),
    companyExtractFileId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    registeredOffice: Address,
    principalPlaceOfBusiness: Address,
    listedExchange: z.string(),
    ticker: z.string(),
    ultimateBeneficialOwnerIds: z.array(z.string()),
    directorIds: z.array(z.string()),
  })
  .partial()
  .passthrough();
const CustomerResponse = z
  .object({ customer: Customer, validationResults: z.array(ValidationResult) })
  .partial()
  .passthrough();
const UpdateCompanyRequest = z
  .object({
    entityType: z.string(),
    entityStatus: z.string(),
    companyName: z.string(),
    companyNumber: z.string(),
    businessNumber: z.string(),
    companyExtractFileId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    registeredOffice: Address,
    principalPlaceOfBusiness: Address,
    listedExchange: z.string(),
    ticker: z.string(),
    ultimateBeneficialOwnerIds: z.array(z.string()),
    directorIds: z.array(z.string()),
    id: z.string(),
    version: z.number().int(),
  })
  .partial()
  .passthrough();
const CustomerListResponse = z
  .object({ customerList: z.array(Customer) })
  .partial()
  .passthrough();
const CustomerRequest = z
  .object({
    customer: Customer,
    entityStatus: z.enum(['InProgress', 'Verified', 'Rejected', 'Draft']).default('InProgress'),
    addToUser: z.boolean(),
    addToUserWhoHasEmail: z.string(),
    reason: z.string(),
    suppressEmailNotification: z.boolean().default(false),
  })
  .partial()
  .passthrough();
const ReviewerUserCustomerRole = z
  .object({
    email: z.string().email(),
    username: z.string(),
    notes: z.string(),
    userPrimaryIndividualId: UUID,
    userPrimaryIndividualName: z.string(),
  })
  .partial()
  .passthrough();
const SyncReviewersRequest = z
  .object({ reviewers: z.array(ReviewerUserCustomerRole) })
  .partial()
  .passthrough();
const IValidatable = z.object({}).partial().passthrough();
const ValidationFailedResponse = z
  .object({
    request: IValidatable.nullable(),
    validationResults: z.array(
      z
        .object({
          path: z.string(),
          name: z.string(),
          resultType: ResultType,
          resultName: z.string(),
          resultScope: ResultScope,
          message: z.string(),
        })
        .partial()
        .passthrough(),
    ),
  })
  .partial()
  .passthrough();
const UserCustomerRole = z.enum(['Controller', 'Viewer']);
const WalletHolderUserCustomerRole = z
  .object({
    email: z.string().email().optional(),
    username: z.string().optional(),
    notes: z.string().optional(),
    userPrimaryIndividualId: UUID.optional(),
    userPrimaryIndividualName: z.string().optional(),
    systemAssignedRole: UserCustomerRole.optional(),
    adminAssignedRole: UserCustomerRole.nullable(),
  })
  .passthrough();
const SyncWalletHoldersRequest = z
  .object({ walletHolders: z.array(WalletHolderUserCustomerRole) })
  .partial()
  .passthrough();
const UserCustomerRoleSource = z.enum(['Manual', 'Owner', 'CustomerReference']);
const UserCustomerRoleResponse = z
  .object({
    email: z.string().email(),
    username: z.string(),
    role: UserCustomerRole,
    source: UserCustomerRoleSource,
    notes: z.string(),
    userPrimaryIndividualId: z
      .string()
      .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
      .uuid(),
    userPrimaryIndividualName: z.string(),
  })
  .partial()
  .passthrough();
const UpdateEntityStatusRequest = z
  .object({ entityStatus: EntityStatus, reason: z.string() })
  .partial()
  .passthrough();
const UpdateIdentityDocumentStatusRequest = z
  .object({
    identityDocumentType: z.string(),
    identityDocumentStatus: z.string(),
    reason: z.string(),
  })
  .partial()
  .passthrough();
const CustomerAddUserRoleRequest = z
  .object({
    username: z.string(),
    email: z.string(),
    role: UserCustomerRole.default('Controller'),
    source: z.enum(['Manual', 'Owner']),
    notes: z.string(),
  })
  .partial()
  .passthrough();
const IdentityPageEligibilityResponse = z.object({ eligible: z.boolean() }).passthrough();
const ScanResultStatus = z.enum([
  'SUCCESS',
  'DOCUMENT_LOOKUP_FAILURE',
  'SCAN_FAILURE',
  'DOCUMENT_UPDATE_FAILURE',
  'NOTHING_TO_SCAN',
  'VERIFICATION_FAILURE',
]);
const ScanResultErrorDetail = z
  .object({ code: z.string(), message: z.string() })
  .partial()
  .passthrough();
const IdentityDocumentScanResult = z
  .object({
    customerId: UUID,
    documentType: z.enum(['Passport', 'MedicareCard', 'DriverLicence', 'PhotoCard', 'ImmiCard']),
    fileIds: z.array(
      z
        .string()
        .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
        .uuid(),
    ),
    status: ScanResultStatus,
    scanTime: OffsetDateTime,
    errorDetails: z.array(ScanResultErrorDetail),
  })
  .partial()
  .passthrough();
const IdentityDocumentType = z.enum([
  'Passport',
  'DriverLicence',
  'OtherIdentityDocument',
  'MedicareCard',
  'PhotoCard',
  'ImmiCard',
]);
const ScanQualityCheck = z.enum([
  'ImageGlares',
  'ImageFocus',
  'ImageResolution',
  'ImageColorness',
  'Perspective',
  'Bounds',
  'Portrait',
  'Brightness',
  'Occlusion',
  'Other',
]);
const ScanDocumentApiResponse = z
  .object({
    type: IdentityDocumentType,
    passport: Passport,
    driverLicence: DriverLicence,
    medicareCard: MedicareCard,
    photoCard: PhotoCard,
    immiCard: ImmiCard,
    medicareCardOwners: z.record(z.string()),
    failedQualityChecks: z.array(ScanQualityCheck),
  })
  .partial()
  .passthrough();
const ScanThresholdResult = z
  .object({
    identityDocumentType: IdentityDocumentType,
    customerId: UUID,
    firstName: z.string(),
    middleName: z.string(),
    lastName: z.string(),
    scanDocumentValues: z
      .object({
        type: IdentityDocumentType,
        passport: Passport,
        driverLicence: DriverLicence,
        medicareCard: MedicareCard,
        photoCard: PhotoCard,
        immiCard: ImmiCard,
        medicareCardOwners: z.record(z.string()),
        failedQualityChecks: z.array(ScanQualityCheck),
      })
      .partial()
      .passthrough(),
    fullNameThreshold: z.number(),
    firstNameThreshold: z.number(),
    familyNameThreshold: z.number(),
  })
  .partial()
  .passthrough();
const MedicareCardScanThresholdResult = z
  .object({ ownerName: z.string(), scanThresholdResult: ScanThresholdResult })
  .partial()
  .passthrough();
const FeatureToggleStatus = z
  .object({ key: z.string(), enabled: z.boolean() })
  .partial()
  .passthrough();
const DocumentType = z.enum([
  'DriverLicence',
  'MedicareCard',
  'OtherIdentityDocument',
  'Passport',
  'CompanyExtract',
  'PartnershipAgreement',
  'SoleTraderSupportDocument',
  'TrustDeed',
  'WholesaleCertificate',
  'RealEstateSupportDocument',
  'OtherSupportDocument',
  'PhotoCard',
  'ImmiCard',
]);
const postV1file_Body = z
  .object({
    entityId: UUID,
    documentType: DocumentType,
    fileType: z.string(),
    file: z.instanceof(File),
  })
  .partial()
  .passthrough();
const FileUploadResponse = z
  .object({ fileId: UUID, validationResults: z.array(ValidationResult) })
  .partial()
  .passthrough();
const FileType = z.enum([
  'DriverLicence',
  'MedicareCard',
  'OtherIdentityDocument',
  'Passport',
  'PhotoCard',
  'ImmiCard',
  'CompanyExtract',
  'PartnershipAgreement',
  'SoleTraderSupportDocument',
  'TrustDeed',
  'WholesaleCertificate',
  'RealEstateSupportDocument',
  'OtherSupportDocument',
  'IdPortrait',
]);
const PresignApiRequest = z
  .object({
    entityId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    documentType: FileType,
    filenameList: z.array(z.string()),
  })
  .partial()
  .passthrough();
const PresignUrl = z
  .object({ entityId: UUID, fileId: UUID, filename: z.string(), url: z.string() })
  .partial()
  .passthrough();
const PresignResponse = z
  .object({ presignUrlList: z.array(PresignUrl) })
  .partial()
  .passthrough();
const FileBucketType = z.enum([
  'DriverLicence',
  'MedicareCard',
  'OtherIdentityDocument',
  'Passport',
  'PhotoCard',
  'ImmiCard',
  'CompanyExtract',
  'PartnershipAgreement',
  'SoleTraderSupportDocument',
  'TrustDeed',
  'WholesaleCertificate',
  'RealEstateSupportDocument',
  'OtherSupportDocument',
  'IdPortrait',
  'Logo',
]);
const FileItemResponse = z
  .object({
    entityId: UUID,
    bucketType: FileBucketType,
    fileId: UUID,
    filename: z.string().optional(),
    objectKey: z.string().optional(),
    lastModified: OffsetDateTime.optional(),
    sizeInBytes: z.number().int().optional(),
    docType: DocumentType.optional(),
  })
  .passthrough();
const FileItemPageResponse = z
  .object({ itemList: z.array(FileItemResponse) })
  .partial()
  .passthrough();
const LogoFileType = z.enum(['JPG', 'JPEG', 'PNG', 'SVG']);
const postV1logoorganisationCodeOrganisationCode_Body = z
  .object({ fileType: LogoFileType, file: z.instanceof(File) })
  .partial()
  .passthrough();
const LogoUploadResponse = z.object({ url: z.string() }).partial().passthrough();
const LogoResponse = z
  .object({ url: z.string(), organisationName: z.string() })
  .partial()
  .passthrough();
const AccessLevel = z.enum(['RestrictedToCustomer', 'RestrictedToClient', 'Unrestricted']);
const MetaRule: z.ZodType<MetaRule> = z.lazy(() =>
  z
    .object({ metaEntity: MetaEntity, metaAttribute: MetaAttribute, ruleType: z.string() })
    .partial()
    .passthrough(),
);
const MetaAttribute: z.ZodType<MetaAttribute> = z.lazy(() =>
  z
    .object({
      name: z.string(),
      dataType: z.string(),
      label: z.string(),
      description: z.string(),
      accessLevel: AccessLevel,
      createOnly: z.boolean(),
      readOnly: z.boolean(),
      readRoles: z.array(z.string()),
      writeRoles: z.array(z.string()),
      ruleList: z.array(MetaRule),
    })
    .partial()
    .passthrough(),
);
const MetaEntity: z.ZodType<MetaEntity> = z.lazy(() =>
  z
    .object({
      entityType: z.string(),
      attributeList: z.array(MetaAttribute),
      ruleList: z.array(MetaRule),
    })
    .partial()
    .passthrough(),
);
const Organisation: z.ZodType<Organisation> = z.lazy(() =>
  z
    .object({
      id: z.string(),
      version: z.number().int(),
      organisationType: z.literal('Client'),
      entityId: z.string(),
      organisationStatus: z.enum(['Active', 'Inactive']),
      organisationName: z.string(),
      children: z.array(Organisation),
    })
    .partial()
    .passthrough(),
);
const OrganisationPageResponse = z
  .object({
    startIndex: z.number().int(),
    lastIndex: z.number().int(),
    itemCount: z.number().int(),
    itemList: z.array(Organisation),
  })
  .partial()
  .passthrough();
const OrganisationResponse = z
  .object({ organisation: Organisation, validationResults: z.array(ValidationResult) })
  .partial()
  .passthrough();
const OrganisationCodeStatus = z.enum(['Active', 'Inactive']);
const OnboardingWorkflowType = z.enum(['DEFAULT', 'INDIVIDUAL_ONLY', 'ORGANISATION_ONLY']);
const OrganisationCode = z
  .object({
    id: UUID,
    code: z.string(),
    organisations: z.array(Organisation),
    signUpUrl: z.string(),
    qrCodeUrl: z.string(),
    status: OrganisationCodeStatus,
    onboardingWorkflowType: OnboardingWorkflowType,
    createdDateTime: OffsetDateTime,
    createdBy: z.string(),
    lastUpdatedDateTime: OffsetDateTime,
    lastUpdatedBy: z.string(),
    returnUrl: z.string().nullable(),
    redirectAllowedOrigins: z.array(z.string()).nullable(),
    voEnabled: z.boolean(),
    optedInDocumentTypes: z.array(IdentityDocumentType),
    individualOnly: z.boolean(),
  })
  .partial()
  .passthrough();
const UpdateOptedInDocumentTypesRequest = z
  .object({ optedInDocumentTypes: z.array(IdentityDocumentType).nullable() })
  .partial()
  .passthrough();
const UpdateRedirectAllowedOriginsRequest = z
  .object({ allowedOrigins: z.array(z.string()).nullable() })
  .partial()
  .passthrough();
const UpdateReturnUrlRequest = z
  .object({ returnUrl: z.string().url().nullable() })
  .partial()
  .passthrough();
const UpdateVoEnabledRequest = z.object({ voEnabled: z.boolean() }).passthrough();
const UpdateWorkflowTypeRequest = z.object({ workflowType: OnboardingWorkflowType }).passthrough();
const UpdateOrganisationNotificationPreferenceRequest = z
  .object({ version: z.number().int(), detail: z.string() })
  .passthrough();
const OrganisationNotificationPreferenceType = z.enum(['EMAIL', 'WEBHOOK']);
const OrganisationNotificationPreferenceStatus = z.enum(['ACTIVE', 'INACTIVE']);
const OrganisationNotificationPreferenceResponse = z
  .object({
    id: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    version: z.number().int(),
    organisationId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    type: OrganisationNotificationPreferenceType,
    detail: z.string(),
    status: OrganisationNotificationPreferenceStatus,
  })
  .partial()
  .passthrough();
const OrgSummarySearchResult = z
  .object({
    reportingEntityUUID: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    reportingEntityId: z.string(),
    reportingEntityName: z.string(),
    subEntityUUID: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    subEntityId: z.string(),
    subEntityName: z.string(),
  })
  .partial()
  .passthrough();
const OrganisationModifyUserRequest = z.object({ username: z.string() }).passthrough();
const UserSummary = z
  .object({
    id: UUID,
    username: z.string(),
    email: z.string().nullable(),
    withCredential: z.boolean(),
  })
  .partial()
  .passthrough();
const CreateOrganisationCodeRequest = z
  .object({
    organisationIds: z.array(
      z
        .string()
        .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
        .uuid(),
    ),
  })
  .partial()
  .passthrough();
const CreateOrganisationNotificationPreferenceRequest = z
  .object({ detail: z.string() })
  .passthrough();
const CreatePartnershipRequest = z
  .object({
    entityType: z.string(),
    entityStatus: z.string(),
    partnershipName: z.string(),
    businessNumber: z.string(),
    principalPlaceOfBusiness: Address,
    partnerIds: z.array(z.string()),
  })
  .partial()
  .passthrough();
const UpdatePartnershipRequest = z
  .object({
    entityType: z.string(),
    entityStatus: z.string(),
    partnershipName: z.string(),
    businessNumber: z.string(),
    principalPlaceOfBusiness: Address,
    partnerIds: z.array(z.string()),
    id: z.string(),
    version: z.number().int(),
  })
  .partial()
  .passthrough();
const PEPSanction = z
  .object({
    id: UUID,
    version: z.number().int(),
    customerId: UUID,
    pepStatus: z.boolean(),
    sanctionStatus: z.boolean(),
    lastUpdatedDatetime: OffsetDateTime,
    lastUpdatedBy: z.string(),
    commentText: z.string(),
  })
  .partial()
  .passthrough();
const PEPSanctionPageResponse = z
  .object({
    startIndex: z.number().int(),
    lastIndex: z.number().int(),
    itemCount: z.number().int(),
    itemList: z.array(PEPSanction),
  })
  .partial()
  .passthrough();
const PEPSanctionResponse = z
  .object({ pepSanction: PEPSanction, validationResults: z.array(ValidationResult) })
  .partial()
  .passthrough();
const PermissionStatus = z.enum(['Active', 'Inactive']);
const Permission = z
  .object({
    id: UUID,
    version: z.number().int(),
    customerId: UUID,
    organisationId: UUID,
    controllerId: UUID,
    controllerName: z.string(),
    reportingEntityId: z.string(),
    reportingEntityName: z.string(),
    subEntityId: z.string(),
    subEntityName: z.string(),
    permissionStatus: PermissionStatus,
    adminGranted: z.boolean(),
    addedDate: OffsetDateTime,
    addedBy: z.string(),
    removedDate: OffsetDateTime,
    removedBy: z.string(),
  })
  .partial()
  .passthrough();
const CreatePermissionRequest = z
  .object({
    customerId: UUID,
    organisationId: UUID,
    controllerId: UUID,
    adminOverride: z.boolean(),
  })
  .partial()
  .passthrough();
const PermissionResponse = z
  .object({ permission: Permission, validationResults: z.array(ValidationResult) })
  .partial()
  .passthrough();
const SummaryFormat = z.enum(['json', 'xlsx']);
const format = z.unknown().and(SummaryFormat).optional();
const OrderBy1 = z.enum([
  'name',
  'status',
  'entityType',
  'wholesaleCertificateStatus',
  'currentRiskTitle',
  'currentRiskScore',
  'lastAssessment',
  'referenceData',
]);
const orderBy = z.unknown().and(OrderBy1).optional();
const OrderDirection = z.enum(['asc', 'desc']);
const orderDirection = z.unknown().and(OrderDirection).optional();
const EntityType = z.enum([
  'Individual_Domestic',
  'Individual_Foreign',
  'SMSF',
  'Sole_Trader',
  'Partnership',
  'Private_Company',
  'Private_Foreign_Company',
  'Public_Company',
  'Public_Foreign_Company',
  'Trust_Regulated',
  'Trust_Unregulated',
  'Charity_Association',
]);
const CurrentRiskTitle = z.enum(['Pending', 'Low', 'Medium', 'High', 'Extreme']);
const PermissionCustomerSummary = z
  .object({
    permissionId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    addedDate: OffsetDateTime.datetime({ offset: true }),
    removedDate: OffsetDateTime.datetime({ offset: true }),
    permissionStatus: PermissionStatus,
    customerId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    entityType: EntityType,
    customerName: z.string(),
    yearsSinceRemoved: z.number().int(),
    wholesaleCertificate: z.string(),
    currentRiskTitle: CurrentRiskTitle,
    currentRiskScore: z.number().int(),
    lastAssessment: OffsetDateTime.datetime({ offset: true }),
    entityStatus: EntityStatus,
    referenceData: z.string(),
  })
  .partial()
  .passthrough();
const PermissionCustomerSummaryPageResponse = z
  .object({
    startIndex: z.number().int(),
    lastIndex: z.number().int(),
    itemCount: z.number().int(),
    itemList: z.array(PermissionCustomerSummary),
  })
  .partial()
  .passthrough();
const PermissionRequestType = z.enum([
  'Legacy',
  'Individual',
  'SMSF',
  'Sole_Trader',
  'Partnership',
  'Private_Company',
  'Public_Company',
  'Trust_Regulated',
  'Trust_Unregulated',
]);
const PermissionRequestSummary = z
  .object({
    id: UUID.optional(),
    createdDateTime: OffsetDateTime.optional(),
    completedDateTime: OffsetDateTime.optional(),
    userEntityId: UUID.optional(),
    organisationId: UUID.optional(),
    organisationName: z.string().optional(),
    parentOrganisationId: UUID.optional(),
    parentOrganisationName: z.string().optional(),
    permissionId: UUID.optional(),
    onboardingWorkflowType: OnboardingWorkflowType.optional(),
    requestType: PermissionRequestType,
    individualOnly: z.boolean().optional(),
  })
  .passthrough();
const PermissionRequestRequest = z
  .object({
    userEntityId: UUID.optional(),
    username: z.string().optional(),
    email: z.string().optional(),
    organisationIdList: z.array(
      z
        .string()
        .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
        .uuid(),
    ),
    onboardingWorkflowType: OnboardingWorkflowType.optional(),
    individualOnly: z.boolean().optional(),
  })
  .passthrough();
const PermissionRequestResponse = z
  .object({ validationResults: z.array(ValidationResult) })
  .partial()
  .passthrough();
const DocumentVerificationStatus = z.enum([
  'Pending',
  'Passed',
  'Failed',
  'SystemError',
  'DataError',
  'NotApplicable',
]);
const DocumentVerificationError = z
  .object({ field: z.string(), message: z.string(), source: z.string() })
  .partial()
  .passthrough();
const DocumentVerificationResultErrors = z
  .object({ name: z.string(), errorDetails: z.array(DocumentVerificationError) })
  .partial()
  .passthrough();
const IdentityDocumentVerificationResult = z
  .object({
    verificationId: UUID,
    customerId: UUID,
    documentType: IdentityDocumentType,
    status: DocumentVerificationStatus,
    errors: DocumentVerificationResultErrors,
  })
  .partial()
  .passthrough();
const UpdateVerificationResultRequest = z
  .object({
    status: DocumentVerificationStatus,
    errors: DocumentVerificationResultErrors.optional(),
  })
  .passthrough();
const IdentityDocumentVerificationStatusOverrideRequest = z
  .object({ status: z.enum(['Passed', 'Failed', 'DataError', 'NotApplicable']) })
  .partial()
  .passthrough();
const LivenessCheckTransaction = z.object({ transactionId: UUID }).passthrough();
const DocumentConfidenceLevel = z.enum(['NotCompleted', 'Low', 'Substantial', 'High', 'VeryHigh']);
const IdentityConfidenceDocument = z
  .object({
    level: DocumentConfidenceLevel,
    lastUpdatedOn: OffsetDateTime,
    problemDocumentTypes: z.array(IdentityDocumentType),
  })
  .partial()
  .passthrough();
const BiometricConfidenceLevel = z.enum(['NotCompleted', 'InProgress', 'Low', 'High']);
const IdentityConfidenceBiometric = z
  .object({ level: BiometricConfidenceLevel, lastUpdatedOn: OffsetDateTime })
  .partial()
  .passthrough();
const IdentityConfidenceResponse = z
  .object({ Document: IdentityConfidenceDocument, Biometric: IdentityConfidenceBiometric })
  .partial()
  .passthrough();
const VerificationPipelineStatus = z.enum(['Pass', 'Fail', 'Incomplete']);
const VerificationPipelineResponseItem = z
  .object({
    status: VerificationPipelineStatus,
    lastUpdatedOn: OffsetDateTime.datetime({ offset: true }),
  })
  .partial()
  .passthrough();
const VerificationPipelineResponseDocumentDetails = z
  .object({
    DriverLicence: VerificationPipelineResponseItem,
    MedicareCard: VerificationPipelineResponseItem,
    OtherIdentityDocument: VerificationPipelineResponseItem,
    Passport: VerificationPipelineResponseItem,
    PhotoCard: VerificationPipelineResponseItem,
    ImmiCard: VerificationPipelineResponseItem,
  })
  .partial()
  .passthrough();
const VerificationPipelineResponseItemWithDetails = z
  .object({
    status: VerificationPipelineStatus,
    lastUpdatedOn: OffsetDateTime.datetime({ offset: true }),
    details: VerificationPipelineResponseDocumentDetails,
  })
  .partial()
  .passthrough();
const VerificationPipelineResponseDocument = z
  .object({
    Capture: VerificationPipelineResponseItemWithDetails,
    DataMatch: VerificationPipelineResponseItemWithDetails,
  })
  .partial()
  .passthrough();
const VerificationPipelineResponseDocumentCategory = z
  .object({ pipeline: VerificationPipelineResponseDocument, status: VerificationPipelineStatus })
  .partial()
  .passthrough();
const VerificationPipelineResponseFaceCheck = z
  .object({
    Liveness: VerificationPipelineResponseItem,
    PhotoMatch: VerificationPipelineResponseItem,
  })
  .partial()
  .passthrough();
const VerificationPipelineResponseFaceCheckCategory = z
  .object({ pipeline: VerificationPipelineResponseFaceCheck, status: VerificationPipelineStatus })
  .partial()
  .passthrough();
const VerificationPipelineResponse = z
  .object({
    Document: VerificationPipelineResponseDocumentCategory,
    FaceCheck: VerificationPipelineResponseFaceCheckCategory,
  })
  .partial()
  .passthrough();
const CompareFacesResult = z.enum([
  'COMPLETED',
  'SOURCE_IMAGE_CONFIDENCE_LOWER_THAN_MINIMAL',
  'TARGET_IMAGE_CONFIDENCE_LOWER_THAN_MINIMAL',
  'MORE_THAN_ONE_FACE_DETECTED',
  'NO_FACE_DETECTED',
  'FAILURE',
]);
const PortraitComparisonResponse = z
  .object({ customerId: UUID, result: CompareFacesResult, similarity: z.number() })
  .partial()
  .passthrough();
const AuthorizedIssuer = z
  .object({ name: z.string(), alpha2: z.string(), alpha3: z.string() })
  .partial()
  .passthrough();
const IIdentityDocument = z
  .object({
    country: AuthorizedIssuer.nullable(),
    documentType: z.string(),
    captureStatus: IdentityDocumentCaptureStatus,
  })
  .partial()
  .passthrough();
const IdentityDocumentRequest = z
  .object({
    identityDocumentType: z.string(),
    driverLicence: DriverLicence,
    medicareCard: MedicareCard,
    otherIdentityDocument: OtherIdentityDocument,
    passport: Passport,
    photoCard: PhotoCard,
    immiCard: ImmiCard,
    identityDocument: IIdentityDocument,
  })
  .partial()
  .passthrough();
const UpdatePortraitRequest = z
  .object({
    fileId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
  })
  .partial()
  .passthrough();
const IdentityPortraitItemResponse = z
  .object({ customerId: UUID, portraitUrl: z.string() })
  .partial()
  .passthrough();
const FileItem = z
  .object({
    partition: z.string(),
    bucketType: FileBucketType,
    fileId: UUID,
    filename: z.string().optional(),
    objectKey: z.string().optional(),
    lastModified: OffsetDateTime.optional(),
    sizeInBytes: z.number().int().optional(),
    docType: DocumentType.optional(),
  })
  .passthrough();
const WholesaleCertificateUpdateRequest = z
  .object({ dateIssued: z.string(), dateOfExpiry: z.string(), accountantNumber: z.string() })
  .partial()
  .passthrough();
const WholesaleCertificateCreateRequest = z.object({ fileId: z.string() }).partial().passthrough();
const WholesaleCertificateStatusRequest = z
  .object({ certificateStatus: z.string(), reason: z.string() })
  .partial()
  .passthrough();
const AuthorizedIssuerReferenceResponse = z
  .object({ name: z.string(), alpha2: z.string().nullable(), alpha3: z.string() })
  .partial()
  .passthrough();
const CountryReferenceResponse = z
  .object({ name: z.string(), alpha2: z.string(), alpha3: z.string() })
  .partial()
  .passthrough();
const ReportCapabilityResponse = z.object({ downloadUrl: z.string() }).partial().passthrough();
const ReportRequestStatus = z.enum(['PENDING', 'NOTIFIED', 'FAILED']);
const ReportRequestResponse = z
  .object({
    id: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    status: ReportRequestStatus,
  })
  .partial()
  .passthrough();
const RiskLevel = z.object({ title: z.string(), score: z.number().int() }).partial().passthrough();
const RiskMitigationTemplate = z
  .object({
    id: z.string(),
    version: z.number().int(),
    organisationId: z.string(),
    title: z.string(),
    description: z.string(),
    ocddFrequencyInMonths: z.number().int(),
    status: z.string(),
  })
  .partial()
  .passthrough();
const RuleCriteria = z
  .object({ attributeName: z.string(), comparison: z.string(), valueList: z.array(z.string()) })
  .partial()
  .passthrough();
const RiskAssessmentRule = z
  .object({
    id: z.string(),
    version: z.number().int(),
    organisationId: z.string(),
    title: z.string(),
    description: z.string(),
    inherentRisk: RiskLevel,
    residualRisk: RiskLevel,
    status: z.string(),
    mitigationList: z.array(RiskMitigationTemplate),
    criteriaList: z.array(RuleCriteria),
  })
  .partial()
  .passthrough();
const RiskMitigationComment = z
  .object({
    lastUpdatedDatetime: z.string(),
    lastUpdatedBy: z.string(),
    listIndex: z.number().int(),
    commentText: z.string(),
  })
  .partial()
  .passthrough();
const RiskMitigation = z
  .object({
    id: z.string(),
    version: z.number().int(),
    organisationId: z.string(),
    templateId: z.string(),
    title: z.string(),
    description: z.string(),
    commentList: z.array(RiskMitigationComment),
    lastUpdatedDatetime: z.string(),
    lastUpdatedBy: z.string(),
    mitigationStatus: z.string(),
    ocddFrequencyInMonths: z.number().int(),
    associatedRuleList: z.array(RiskAssessmentRule),
  })
  .partial()
  .passthrough();
const RiskAssessment = z
  .object({
    id: z.string(),
    version: z.number().int(),
    organisationId: z.string(),
    customerId: z.string(),
    createdDatetime: z.string(),
    lastUpdatedDatetime: z.string(),
    assessedRisk: RiskLevel,
    potentialRisk: RiskLevel,
    currentRisk: RiskLevel,
    status: z.string(),
    permissionId: z.string(),
    ruleList: z.array(RiskAssessmentRule),
    mitigations: z.array(RiskMitigation),
  })
  .partial()
  .passthrough();
const RiskAssessmentResponse = z
  .object({ riskAssessment: RiskAssessment, validationResults: z.array(ValidationResult) })
  .partial()
  .passthrough();
const RiskAssessmentPageResponse = z
  .object({
    startIndex: z.number().int(),
    lastIndex: z.number().int(),
    itemCount: z.number().int(),
    itemList: z.array(RiskAssessment),
  })
  .partial()
  .passthrough();
const RiskAssessmentRuleResponse = z
  .object({ riskAssessmentRule: RiskAssessmentRule, validationResults: z.array(ValidationResult) })
  .partial()
  .passthrough();
const RiskAssessmentRulePageResponse = z
  .object({
    startIndex: z.number().int(),
    lastIndex: z.number().int(),
    itemCount: z.number().int(),
    itemList: z.array(RiskAssessmentRule),
  })
  .partial()
  .passthrough();
const RiskAssessmentRuleTemplateRequest = z
  .object({ ruleId: z.string(), templateIdList: z.array(z.string()) })
  .partial()
  .passthrough();
const RiskAssessmentProcessRequest = z
  .object({
    clientOrgId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    customerId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
  })
  .partial()
  .passthrough();
const RiskAssessmentSummary = z
  .object({
    riskAssessmentId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    organisationId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    customerId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    createdDatetime: OffsetDateTime.datetime({ offset: true }),
    lastUpdatedDatetime: OffsetDateTime.datetime({ offset: true }),
    customerName: z.string(),
    entityType: z.string(),
    assessedRiskLevel: RiskLevel,
    currentRiskLevel: RiskLevel,
    assessmentStatus: z.string(),
  })
  .partial()
  .passthrough();
const PageResultRiskAssessmentSummary = z
  .object({
    startIndex: z.number().int(),
    lastIndex: z.number().int(),
    itemCount: z.number().int(),
    itemList: z.array(RiskAssessmentSummary),
  })
  .partial()
  .passthrough();
const RiskMitigationTemplateResponse = z
  .object({
    riskMitigationTemplate: RiskMitigationTemplate,
    validationResults: z.array(ValidationResult),
  })
  .partial()
  .passthrough();
const RiskMitigationTemplatePageResponse = z
  .object({
    startIndex: z.number().int(),
    lastIndex: z.number().int(),
    itemCount: z.number().int(),
    itemList: z.array(RiskMitigationTemplate),
  })
  .partial()
  .passthrough();
const RiskMitigationResponse = z
  .object({ riskMitigation: RiskMitigation, validationResults: z.array(ValidationResult) })
  .partial()
  .passthrough();
const SupportDocumentPaginationOrderBy = z.enum([
  'status',
  'documentType',
  'entityName',
  'submissionDateTime',
]);
const orderBy__2 = z.unknown().and(SupportDocumentPaginationOrderBy).optional();
const SupportDocumentPaginationResponseItem = z
  .object({
    id: UUID,
    documentType: SupportDocumentSubType,
    entityType: EntityType,
    entityId: UUID,
    entityName: z.string(),
    status: EntityStatus,
    submissionDateTime: OffsetDateTime,
  })
  .partial()
  .passthrough();
const SupportDocumentPaginationResponse = z
  .object({
    startIndex: z.number().int(),
    lastIndex: z.number().int(),
    totalCount: z.number().int(),
    itemList: z.array(SupportDocumentPaginationResponseItem),
  })
  .partial()
  .passthrough();
const CertificateOfTitle = z
  .object({
    id: UUID,
    version: z.number().int(),
    fileId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    supportDocumentSubType: SupportDocumentSubType,
    status: EntityStatus,
    createdDate: OffsetDateTime,
    createdBy: z.string(),
    lastUpdatedDate: OffsetDateTime,
    lastUpdatedBy: z.string(),
    customerId: UUID,
    keyAttributeName: z.string(),
    documentType: z.enum([
      'RealEstateSupportDocument',
      'OtherSupportDocument',
      'WholesaleCertificate',
    ]),
    dateOfIssue: LocalDate,
    volume: z.string(),
    folio: z.string(),
    descriptionOfLand: z.string(),
    propertyOwners: z.array(z.string()),
  })
  .partial()
  .passthrough();
const NationalVendorDeclarationForLivestock = z
  .object({
    id: UUID,
    version: z.number().int(),
    fileId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    supportDocumentSubType: SupportDocumentSubType,
    status: EntityStatus,
    createdDate: OffsetDateTime,
    createdBy: z.string(),
    lastUpdatedDate: OffsetDateTime,
    lastUpdatedBy: z.string(),
    customerId: UUID,
    keyAttributeName: z.string(),
    documentType: z.enum([
      'RealEstateSupportDocument',
      'OtherSupportDocument',
      'WholesaleCertificate',
    ]),
    declarationDate: LocalDate,
    propertyIdentificationCode: z.string(),
  })
  .partial()
  .passthrough();
const OtherSupportDocument = z
  .object({
    id: UUID,
    version: z.number().int(),
    fileId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    supportDocumentSubType: SupportDocumentSubType,
    status: EntityStatus,
    createdDate: OffsetDateTime,
    createdBy: z.string(),
    lastUpdatedDate: OffsetDateTime,
    lastUpdatedBy: z.string(),
    customerId: UUID,
    keyAttributeName: z.string(),
    documentType: z.enum([
      'RealEstateSupportDocument',
      'OtherSupportDocument',
      'WholesaleCertificate',
    ]),
    otherSupportDocumentType: z.string(),
    comment: z.string(),
  })
  .partial()
  .passthrough();
const RatesNotice = z
  .object({
    id: UUID,
    version: z.number().int(),
    fileId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    supportDocumentSubType: SupportDocumentSubType,
    status: EntityStatus,
    createdDate: OffsetDateTime,
    createdBy: z.string(),
    lastUpdatedDate: OffsetDateTime,
    lastUpdatedBy: z.string(),
    customerId: UUID,
    keyAttributeName: z.string(),
    documentType: z.enum([
      'RealEstateSupportDocument',
      'OtherSupportDocument',
      'WholesaleCertificate',
    ]),
    dateOfIssue: LocalDate,
    addressOfProperty: z.string(),
    descriptionOfLand: z.string(),
    propertyOwners: z.array(z.string()),
    valuationDate: LocalDate,
    valuation: z.number().int(),
  })
  .partial()
  .passthrough();
const ValuationNotice = z
  .object({
    id: UUID,
    version: z.number().int(),
    fileId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
    supportDocumentSubType: SupportDocumentSubType,
    status: EntityStatus,
    createdDate: OffsetDateTime,
    createdBy: z.string(),
    lastUpdatedDate: OffsetDateTime,
    lastUpdatedBy: z.string(),
    customerId: UUID,
    keyAttributeName: z.string(),
    documentType: z.enum([
      'RealEstateSupportDocument',
      'OtherSupportDocument',
      'WholesaleCertificate',
    ]),
    landValueAsAt: LocalDate,
    landValue: z.number().int(),
    ownersOrLessees: z.array(z.string()),
    addressOfProperty: z.string(),
    descriptionOfLand: z.string(),
  })
  .partial()
  .passthrough();
const SupportDocument = z.union([
  CertificateOfTitle,
  NationalVendorDeclarationForLivestock,
  OtherSupportDocument,
  RatesNotice,
  ValuationNotice,
  WholesaleCertificate,
]);
const CreateSupportDocumentRequest = z
  .object({
    supportDocumentSubType: SupportDocumentSubType,
    fileId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
  })
  .partial()
  .passthrough();
const SupportDocumentSubTypeWithAllowedStatus = z
  .object({ supportDocumentSubType: SupportDocumentSubType, allowed: z.boolean() })
  .partial()
  .passthrough();
const UpdateSupportDocumentRequest = z
  .object({
    fileId: UUID.regex(
      /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/,
    ).uuid(),
  })
  .partial()
  .passthrough();
const UpdateSupportDocumentStatusRequest = z
  .object({ status: EntityStatus, reason: z.string() })
  .partial()
  .passthrough();
const TaskType = z.enum([
  'FACE_CHECK',
  'WHOLESALE_CERTIFICATE',
  'REVIEW_REJECTED_IDENTITY',
  'CREDENTIAL_SETUP',
]);
const TaskConfiguration = z.object({}).partial().passthrough();
const TaskStatus = z.enum(['ACTIVE', 'INACTIVE', 'FAILED', 'SUCCEEDED']);
const TaskLookupResponse = z
  .object({
    id: UUID,
    customerId: UUID,
    type: TaskType,
    configuration: TaskConfiguration,
    status: TaskStatus,
    createdDateTime: OffsetDateTime,
    createdBy: z.string(),
    trackingReferenceUrl: z.string(),
  })
  .partial()
  .passthrough();
const TaskCreationRequest = z
  .object({ type: TaskType, configuration: TaskConfiguration.optional() })
  .passthrough();
const TaskCreatedResponse = z
  .object({
    id: UUID,
    customerId: UUID,
    type: TaskType,
    configuration: TaskConfiguration,
    status: TaskStatus,
    createdDateTime: OffsetDateTime,
    createdBy: z.string(),
  })
  .partial()
  .passthrough();
const EmptyTaskCreationRequest = z.object({}).partial().passthrough();
const TrackingReferenceReportClient = z
  .object({
    organisationId: UUID,
    organisationName: z.string(),
    organisationCount: z.number().int(),
  })
  .partial()
  .passthrough();
const TrackingReferenceReportClientPageResponse = z
  .object({
    startIndex: z.number().int(),
    lastIndex: z.number().int(),
    itemCount: z.number().int(),
    itemList: z.array(TrackingReferenceReportClient),
  })
  .partial()
  .passthrough();
const TrackingReferenceReportFormat = z.enum(['json', 'internalXlsx', 'clientXlsx']);
const format__2 = z.unknown().and(TrackingReferenceReportFormat).optional();
const TrackingReferenceStatus = z.enum(['Active', 'Inactive', 'Completed']);
const TrackingReferenceReportRow = z
  .object({
    trackingReferenceId: UUID,
    organisationName: z.string(),
    email: z.string(),
    invitedAt: OffsetDateTime,
    daysSinceInvited: z.number().int(),
    referenceNo: z.number().int(),
    isLatest: z.boolean(),
    status: TrackingReferenceStatus,
    identityCreated: z.boolean(),
    entities: z.string(),
    entitiesWithStatus: z.string(),
    metadata: z.string().nullable(),
  })
  .passthrough();
const TrackingReferenceReportPageResponse = z
  .object({
    startIndex: z.number().int(),
    lastIndex: z.number().int(),
    itemCount: z.number().int(),
    itemList: z.array(TrackingReferenceReportRow),
  })
  .passthrough();
const TrackingReferenceActivationRequest = z
  .object({ requestedOnboardAsMode: z.string() })
  .partial()
  .passthrough();
const TrackingReferenceType = z.enum(['TASK', 'SIGN_UP']);
const TrackingReferenceActivationResponse = z
  .object({
    type: TrackingReferenceType,
    taskId: UUID.optional(),
    onboardAsMode: z.string().nullish(),
    declaredEntityType: z.string().nullish(),
  })
  .passthrough();
const EntityConfirmation = z
  .object({
    entityType: z.enum([
      'Individual',
      'SMSF',
      'Sole_Trader',
      'Partnership',
      'Private_Company',
      'Public_Company',
      'Trust_Regulated',
      'Trust_Unregulated',
    ]),
  })
  .passthrough();
const JoinableInviteResponse = z
  .object({
    trackingReferenceId: UUID,
    opened: z.boolean(),
    declaredEntityType: z.string().nullish(),
    declaredOrganisationName: z.string().nullish(),
  })
  .passthrough();
const JoinableInvitesResponse = z
  .object({ invites: z.array(JoinableInviteResponse), governingDeclaration: z.string().nullish() })
  .passthrough();
const TrackingReferenceOwnershipResponse = z.object({ owned: z.boolean() }).passthrough();
const CreateTrustRequest = z
  .object({
    entityType: z.string(),
    entityStatus: z.string(),
    address: Address,
    businessNumber: z.string(),
    trustName: z.string(),
    businessNameOfTrustee: z.string(),
    trustType: TrustType,
    ultimateBeneficialOwnerIds: z.array(z.string()),
    trusteeIds: z.array(z.string()),
    settlorIds: z.array(z.string()),
  })
  .partial()
  .passthrough();
const UpdateTrustRequest = z
  .object({
    entityType: z.string(),
    entityStatus: z.string(),
    address: Address,
    businessNumber: z.string(),
    trustName: z.string(),
    businessNameOfTrustee: z.string(),
    trustType: TrustType,
    ultimateBeneficialOwnerIds: z.array(z.string()),
    trusteeIds: z.array(z.string()),
    settlorIds: z.array(z.string()),
    id: z.string(),
    version: z.number().int(),
  })
  .partial()
  .passthrough();
const CreateUserRequest = z
  .object({
    preferredName: z.string(),
    email: z.string(),
    organisationId: z.string(),
    sendInvite: z.boolean(),
  })
  .partial()
  .passthrough();
const UserResponse = z
  .object({ userName: z.string(), roles: z.array(z.string()) })
  .partial()
  .passthrough();
const CustomerContext = z
  .object({
    fullName: z.string(),
    firstName: z.string(),
    familyName: z.string(),
    middleName: z.string(),
    dateOfBirth: z.string(),
    preferredContactNumber: z.string(),
    address: z.string(),
    callbackUrl: z.string(),
  })
  .partial()
  .passthrough();
const DeclaredEntityType = z.enum([
  'Individual',
  'Sole_Trader',
  'Partnership',
  'Private_Company',
  'Public_Company',
  'SMSF',
  'Trust_Unregulated',
  'Trust_Regulated',
  'BUSINESS',
  'COMPANY',
  'TRUST',
  'ANY_ORGANISATION',
]);
const UserInviteRequest = z
  .object({
    email: z.string(),
    code: z.string(),
    referenceData: z.string().optional(),
    customerContext: CustomerContext.optional(),
    sendInvitationEmail: z.boolean().optional().default(false),
    declaredEntityType: DeclaredEntityType.optional(),
    declaredOrganisationName: z.string().max(255).optional(),
  })
  .passthrough();
const UserInviteResponse = z
  .object({ trackingReference: z.string(), inviteUrl: z.string(), qrCodeUrl: z.string() })
  .partial()
  .passthrough();
const SelfSignUpRequest = z.object({ email: z.string(), code: z.string() }).passthrough();
const SelfSignUpResponse = z
  .object({ trackingReference: z.string(), invitationSent: z.boolean() })
  .partial()
  .passthrough();
const InvitationTrackingActionRequest = z.object({ email: z.string() }).partial().passthrough();
const InvitationTrackingAction = z.enum(['MAGIC_LINK', 'LOGIN']);
const InvitationTrackingActionResponse = z
  .object({ action: InvitationTrackingAction, trackingReference: z.string() })
  .partial()
  .passthrough();
const TrackingReferenceStatusResponse = z
  .object({ status: TrackingReferenceStatus })
  .partial()
  .passthrough();
const SignupEmailRequest = z
  .object({ preferredName: z.string(), email: z.string(), organisationId: z.string() })
  .partial()
  .passthrough();
const WorkflowMetadata = z
  .object({
    logoUrl: z.string().nullish(),
    onboardingWorkflowType: OnboardingWorkflowType,
    returnUrl: z.string().nullish(),
    redirectUrl: z.string().nullish(),
    identityDocumentCount: z.number().int().nullish(),
  })
  .passthrough();
const WorkflowAssociationType = z.enum([
  'PERMISSION',
  'TRACKING_REFERENCE_SIGNUP',
  'TRACKING_REFERENCE_TASK',
]);
const PermissionDetails = z
  .object({ permissionId: UUID, grantedAt: OffsetDateTime, grantedBy: UUID })
  .passthrough();
const WorkflowMetadataResponse = z
  .object({
    organisationId: UUID,
    organisationName: z.string(),
    organisationCode: z.string(),
    organisationCodeUpdatedDateTime: OffsetDateTime,
    metadata: WorkflowMetadata,
    associationType: WorkflowAssociationType,
    permission: PermissionDetails.nullish(),
  })
  .passthrough();
const WorkflowMetadataPageResponse = z
  .object({
    startIndex: z.number().int(),
    lastIndex: z.number().int(),
    itemCount: z.number().int(),
    itemList: z.array(WorkflowMetadataResponse),
  })
  .partial()
  .passthrough();
const WorkflowMetadataByOrganisationCodeResponse = z
  .object({
    organisationName: z.string(),
    organisationCode: z.string(),
    logoUrl: z.string().nullable(),
    onboardingWorkflowType: OnboardingWorkflowType,
    identityDocumentCount: z.number().int().gte(1).lte(2),
  })
  .passthrough();
const CustomerPaginationOrderBy = z.enum(['status', 'name', 'createdDate', 'updatedDate']);
const orderBy__3 = z.unknown().and(CustomerPaginationOrderBy).optional();
const CustomerPaginationResponseItem = z
  .object({
    id: UUID,
    type: EntityType,
    status: EntityStatus,
    name: z.string(),
    email: z.string(),
    userCustomerAssociationNote: z.string(),
    createdDate: OffsetDateTime,
    updatedDate: OffsetDateTime,
  })
  .partial()
  .passthrough();
const CustomerPaginationResponse = z
  .object({
    beginIndex: z.number().int(),
    endIndex: z.number().int(),
    total: z.number().int(),
    customers: z.array(CustomerPaginationResponseItem),
  })
  .partial()
  .passthrough();
const OrganisationCreateRequest = z
  .object({ organisationName: z.string(), parentEntityId: z.string().optional() })
  .passthrough();
const CreatePEPSanctionRequest = z
  .object({ pepStatus: z.boolean(), sanctionStatus: z.boolean(), commentText: z.string() })
  .passthrough();
const PermissionSummaryResponseFormat = z.enum(['json', 'xlsx']);
const format__3 = z.unknown().and(PermissionSummaryResponseFormat).optional();
const OverrideEffect = z.enum(['None', 'Escalated', 'Mitigated']);
const PermissionCustomerSummaryV2 = z
  .object({
    permissionId: UUID,
    addedDate: OffsetDateTime,
    removedDate: OffsetDateTime,
    permissionStatus: PermissionStatus,
    customerId: UUID,
    entityType: EntityType,
    customerName: z.string(),
    yearsSinceRemoved: z.number().int(),
    wholesaleCertificate: z.string(),
    currentRiskTitle: CurrentRiskTitle,
    currentRiskScore: z.number().int(),
    presentedRiskTitle: CurrentRiskTitle,
    presentedRiskScore: z.number().int(),
    overrideEffect: OverrideEffect,
    lastAssessment: OffsetDateTime,
    entityStatus: EntityStatus,
    referenceData: z.string(),
  })
  .partial()
  .passthrough();
const PermissionCustomerSummaryV2PageResponse = z
  .object({
    startIndex: z.number().int(),
    lastIndex: z.number().int(),
    itemCount: z.number().int(),
    itemList: z.array(PermissionCustomerSummaryV2),
  })
  .partial()
  .passthrough();
const CreatePersonRequest = z
  .object({ entityStatus: z.string(), person: Person, addToUser: z.boolean() })
  .partial()
  .passthrough();
const DocumentTypeChoiceResponse = z
  .object({ type: IdentityDocumentType, rules: z.record(z.boolean()) })
  .partial()
  .passthrough();
const IdentityDocumentRequirementV2Response = z
  .object({
    documentTypeChoices: z.array(DocumentTypeChoiceResponse),
    documentCount: z.number().int(),
  })
  .partial()
  .passthrough();
const LivenessStatus = z.enum(['NO_RESULT', 'CANNOT_CONFIRM', 'CONFIRMED']);
const LivenessTransactionWithPresignedPortraitImageUrlResponse = z
  .object({
    customerId: UUID,
    transactionId: UUID,
    checkedDateTime: OffsetDateTime,
    status: LivenessStatus,
    portraitImageUrl: z.string(),
  })
  .partial()
  .passthrough();
const LivenessTransactionWithPresignedVideoUrlResponse = z
  .object({
    customerId: UUID,
    transactionId: UUID,
    checkedDateTime: OffsetDateTime,
    videoUrl: z.string(),
  })
  .partial()
  .passthrough();
const UpdatePersonRequest = z
  .object({ entityStatus: z.string(), person: Person })
  .partial()
  .passthrough();
const UpdateIdentityDocumentRequest = z
  .object({
    passport: Passport,
    driverLicence: DriverLicence,
    medicareCard: MedicareCard,
    otherIdentityDocument: OtherIdentityDocument,
    photoCard: PhotoCard,
    immiCard: ImmiCard,
  })
  .partial()
  .passthrough();
const IdentityDocumentResponse = z
  .object({
    passport: Passport,
    driverLicence: DriverLicence,
    medicareCard: MedicareCard,
    otherIdentityDocument: OtherIdentityDocument,
    photoCard: PhotoCard,
    immiCard: ImmiCard,
    identityDocumentType: IdentityDocumentType,
  })
  .partial()
  .passthrough();
const UpdateIdentityDocumentCaptureStatusRequest = z
  .object({ status: IdentityDocumentCaptureStatus, reason: z.string().optional() })
  .passthrough();
const IdentityDocumentFileItemResponse = z
  .object({ scanFilesId: UUID, itemList: z.array(FileItemResponse) })
  .partial()
  .passthrough();
const IdentityDocumentUploadPresignResponse = z
  .object({
    scanFilesId: UUID,
    presignUrlList: z.array(PresignUrl),
    itemList: z.array(FileItemResponse),
  })
  .partial()
  .passthrough();
const ScanIdentityDocumentResultResponse = z
  .object({
    identityDocumentScanId: UUID,
    passport: Passport,
    driverLicence: DriverLicence,
    medicareCard: MedicareCard,
    photoCard: PhotoCard,
    immiCard: ImmiCard,
    medicareCardOwners: z.record(z.string()),
    failedQualityChecks: z.array(ScanQualityCheck),
  })
  .partial()
  .passthrough();
const RunAssessmentRequest = z.object({ customerId: UUID }).passthrough();
const AssessmentStatus = z.enum(['NoRisksDetected', 'Open', 'Closed']);
const RuleStatus = z.enum(['Active', 'Inactive']);
const MitigationTemplateStatus = z.enum(['Active', 'Inactive']);
const RiskMitigationTemplateV2 = z
  .object({
    id: UUID,
    organisationId: UUID,
    title: z.string(),
    description: z.string(),
    status: MitigationTemplateStatus,
  })
  .partial()
  .passthrough();
const RuleCriteriaComparison = z.enum([
  'Equals',
  'NotEquals',
  'Contains',
  'NotContains',
  'GreaterThan',
  'LessThan',
  'GreaterThanOrEqualTo',
  'LessThanOrEqualTo',
  'NotEmpty',
  'Empty',
]);
const RuleCriteriaV2 = z
  .object({
    id: UUID,
    attributeName: z.string(),
    comparison: RuleCriteriaComparison,
    valueList: z.array(z.string()),
  })
  .partial()
  .passthrough();
const RiskAssessmentRuleV2 = z
  .object({
    id: UUID,
    organisationId: UUID,
    title: z.string(),
    description: z.string(),
    inherentRisk: RiskLevel,
    residualRisk: RiskLevel,
    status: RuleStatus,
    mitigationList: z.array(RiskMitigationTemplateV2),
    criteriaList: z.array(RuleCriteriaV2),
  })
  .partial()
  .passthrough();
const MitigationStatus = z.enum(['Open', 'Pass', 'Fail', 'Ignore']);
const RiskMitigationCommentV2 = z
  .object({ comment: z.string(), author: z.string(), postedAt: OffsetDateTime })
  .partial()
  .passthrough();
const RiskMitigationV2 = z
  .object({
    id: UUID,
    organisationId: UUID,
    templateId: UUID,
    title: z.string(),
    description: z.string(),
    status: MitigationStatus,
    lastUpdatedDatetime: OffsetDateTime,
    lastUpdatedBy: z.string(),
    commentList: z.array(RiskMitigationCommentV2),
  })
  .partial()
  .passthrough();
const RiskAssessmentV2 = z
  .object({
    id: UUID,
    organisationId: UUID,
    customerId: UUID,
    createdDatetime: OffsetDateTime,
    lastUpdatedDatetime: OffsetDateTime,
    assessedRisk: RiskLevel,
    potentialRisk: RiskLevel,
    currentRisk: RiskLevel,
    presentedRisk: RiskLevel,
    isOverridden: z.boolean(),
    status: AssessmentStatus,
    ruleList: z.array(RiskAssessmentRuleV2),
    mitigations: z.array(RiskMitigationV2),
  })
  .partial()
  .passthrough();
const RiskAssessmentV2PageResponse = z
  .object({
    startIndex: z.number().int(),
    lastIndex: z.number().int(),
    itemCount: z.number().int(),
    itemList: z.array(RiskAssessmentV2),
  })
  .partial()
  .passthrough();
const BlockerCode = z.enum([
  'NO_ACTIVE_PERMISSION',
  'CUSTOMER_NOT_VERIFIED',
  'MISSING_PEP_SANCTION_SCREENING',
  'UNAUTHORISED',
]);
const Blocker = z.object({ code: BlockerCode, message: z.string() }).partial().passthrough();
const EligibilityResponse = z
  .object({ canRun: z.boolean(), blockers: z.array(Blocker) })
  .partial()
  .passthrough();
const RiskAssessmentSummaryV2 = z
  .object({
    riskAssessmentId: UUID,
    organisationId: UUID,
    customerId: UUID,
    customerName: z.string(),
    entityType: z.string(),
    assessedRiskLevel: RiskLevel,
    currentRiskLevel: RiskLevel,
    presentedRiskLevel: RiskLevel,
    isOverridden: z.boolean(),
    assessmentStatus: AssessmentStatus,
    createdDatetime: OffsetDateTime,
    lastUpdatedDatetime: OffsetDateTime,
  })
  .partial()
  .passthrough();
const RiskAssessmentSummaryV2PageResponse = z
  .object({
    startIndex: z.number().int(),
    lastIndex: z.number().int(),
    itemCount: z.number().int(),
    itemList: z.array(RiskAssessmentSummaryV2),
  })
  .partial()
  .passthrough();
const MitigationCommentRequest = z.object({ comment: z.string() }).partial().passthrough();
const MitigationOutcome = z.enum(['Pass', 'Fail', 'Ignore']);
const MitigationStatusRequest = z
  .object({ status: MitigationOutcome, comment: z.string() })
  .partial()
  .passthrough();
const OverrideStatus = z.enum(['ACTIVE', 'DEACTIVATED']);
const RiskAssessmentOverrideResponse = z
  .object({
    overrideRiskLevel: RiskLevel,
    originalRiskLevel: RiskLevel,
    reason: z.string(),
    status: OverrideStatus,
    createdBy: z.string(),
    createdDatetime: OffsetDateTime,
    deactivatedBy: z.string(),
    deactivatedDatetime: OffsetDateTime,
  })
  .partial()
  .passthrough();
const CreateRiskAssessmentOverrideRequest = z
  .object({ overrideRiskLevel: RiskLevel, reason: z.string() })
  .partial()
  .passthrough();

export const schemas = {
  ClientRecordAnnulmentRequest,
  UUID,
  TablePlan,
  ControllerRow,
  ForeignOwned,
  ReportReference,
  SchemaFindings,
  AnnulmentPlan,
  AnnulmentRefusal,
  ClientRecordAnnulmentResponse,
  CustomerRedactionRequest,
  Address,
  IdentityDocumentCaptureStatus,
  DriverLicence,
  MedicareCard,
  OtherIdentityDocument,
  Passport,
  PhotoCard,
  ImmiCard,
  SupportDocumentSubType,
  EntityStatus,
  OffsetDateTime,
  LocalDate,
  WholesaleCertificate,
  Person,
  PersonReference,
  Company,
  CustomerReference,
  TrustType,
  Trust,
  SoleTrader,
  Partnership,
  RedactedCustomer,
  Customer,
  ResultType,
  ResultScope,
  ValidationResult,
  CustomerRedactionResponse,
  IntegrationRegistrationRequest,
  IntegrationRegistrationResponse,
  UserCredentialSetupStatusUpdateRequest,
  AdverseMedia,
  CreateAdverseMediaRequest,
  CreateCompanyRequest,
  CustomerResponse,
  UpdateCompanyRequest,
  CustomerListResponse,
  CustomerRequest,
  ReviewerUserCustomerRole,
  SyncReviewersRequest,
  IValidatable,
  ValidationFailedResponse,
  UserCustomerRole,
  WalletHolderUserCustomerRole,
  SyncWalletHoldersRequest,
  UserCustomerRoleSource,
  UserCustomerRoleResponse,
  UpdateEntityStatusRequest,
  UpdateIdentityDocumentStatusRequest,
  CustomerAddUserRoleRequest,
  IdentityPageEligibilityResponse,
  ScanResultStatus,
  ScanResultErrorDetail,
  IdentityDocumentScanResult,
  IdentityDocumentType,
  ScanQualityCheck,
  ScanDocumentApiResponse,
  ScanThresholdResult,
  MedicareCardScanThresholdResult,
  FeatureToggleStatus,
  DocumentType,
  postV1file_Body,
  FileUploadResponse,
  FileType,
  PresignApiRequest,
  PresignUrl,
  PresignResponse,
  FileBucketType,
  FileItemResponse,
  FileItemPageResponse,
  LogoFileType,
  postV1logoorganisationCodeOrganisationCode_Body,
  LogoUploadResponse,
  LogoResponse,
  AccessLevel,
  MetaRule,
  MetaAttribute,
  MetaEntity,
  Organisation,
  OrganisationPageResponse,
  OrganisationResponse,
  OrganisationCodeStatus,
  OnboardingWorkflowType,
  OrganisationCode,
  UpdateOptedInDocumentTypesRequest,
  UpdateRedirectAllowedOriginsRequest,
  UpdateReturnUrlRequest,
  UpdateVoEnabledRequest,
  UpdateWorkflowTypeRequest,
  UpdateOrganisationNotificationPreferenceRequest,
  OrganisationNotificationPreferenceType,
  OrganisationNotificationPreferenceStatus,
  OrganisationNotificationPreferenceResponse,
  OrgSummarySearchResult,
  OrganisationModifyUserRequest,
  UserSummary,
  CreateOrganisationCodeRequest,
  CreateOrganisationNotificationPreferenceRequest,
  CreatePartnershipRequest,
  UpdatePartnershipRequest,
  PEPSanction,
  PEPSanctionPageResponse,
  PEPSanctionResponse,
  PermissionStatus,
  Permission,
  CreatePermissionRequest,
  PermissionResponse,
  SummaryFormat,
  format,
  OrderBy1,
  orderBy,
  OrderDirection,
  orderDirection,
  EntityType,
  CurrentRiskTitle,
  PermissionCustomerSummary,
  PermissionCustomerSummaryPageResponse,
  PermissionRequestType,
  PermissionRequestSummary,
  PermissionRequestRequest,
  PermissionRequestResponse,
  DocumentVerificationStatus,
  DocumentVerificationError,
  DocumentVerificationResultErrors,
  IdentityDocumentVerificationResult,
  UpdateVerificationResultRequest,
  IdentityDocumentVerificationStatusOverrideRequest,
  LivenessCheckTransaction,
  DocumentConfidenceLevel,
  IdentityConfidenceDocument,
  BiometricConfidenceLevel,
  IdentityConfidenceBiometric,
  IdentityConfidenceResponse,
  VerificationPipelineStatus,
  VerificationPipelineResponseItem,
  VerificationPipelineResponseDocumentDetails,
  VerificationPipelineResponseItemWithDetails,
  VerificationPipelineResponseDocument,
  VerificationPipelineResponseDocumentCategory,
  VerificationPipelineResponseFaceCheck,
  VerificationPipelineResponseFaceCheckCategory,
  VerificationPipelineResponse,
  CompareFacesResult,
  PortraitComparisonResponse,
  AuthorizedIssuer,
  IIdentityDocument,
  IdentityDocumentRequest,
  UpdatePortraitRequest,
  IdentityPortraitItemResponse,
  FileItem,
  WholesaleCertificateUpdateRequest,
  WholesaleCertificateCreateRequest,
  WholesaleCertificateStatusRequest,
  AuthorizedIssuerReferenceResponse,
  CountryReferenceResponse,
  ReportCapabilityResponse,
  ReportRequestStatus,
  ReportRequestResponse,
  RiskLevel,
  RiskMitigationTemplate,
  RuleCriteria,
  RiskAssessmentRule,
  RiskMitigationComment,
  RiskMitigation,
  RiskAssessment,
  RiskAssessmentResponse,
  RiskAssessmentPageResponse,
  RiskAssessmentRuleResponse,
  RiskAssessmentRulePageResponse,
  RiskAssessmentRuleTemplateRequest,
  RiskAssessmentProcessRequest,
  RiskAssessmentSummary,
  PageResultRiskAssessmentSummary,
  RiskMitigationTemplateResponse,
  RiskMitigationTemplatePageResponse,
  RiskMitigationResponse,
  SupportDocumentPaginationOrderBy,
  orderBy__2,
  SupportDocumentPaginationResponseItem,
  SupportDocumentPaginationResponse,
  CertificateOfTitle,
  NationalVendorDeclarationForLivestock,
  OtherSupportDocument,
  RatesNotice,
  ValuationNotice,
  SupportDocument,
  CreateSupportDocumentRequest,
  SupportDocumentSubTypeWithAllowedStatus,
  UpdateSupportDocumentRequest,
  UpdateSupportDocumentStatusRequest,
  TaskType,
  TaskConfiguration,
  TaskStatus,
  TaskLookupResponse,
  TaskCreationRequest,
  TaskCreatedResponse,
  EmptyTaskCreationRequest,
  TrackingReferenceReportClient,
  TrackingReferenceReportClientPageResponse,
  TrackingReferenceReportFormat,
  format__2,
  TrackingReferenceStatus,
  TrackingReferenceReportRow,
  TrackingReferenceReportPageResponse,
  TrackingReferenceActivationRequest,
  TrackingReferenceType,
  TrackingReferenceActivationResponse,
  EntityConfirmation,
  JoinableInviteResponse,
  JoinableInvitesResponse,
  TrackingReferenceOwnershipResponse,
  CreateTrustRequest,
  UpdateTrustRequest,
  CreateUserRequest,
  UserResponse,
  CustomerContext,
  DeclaredEntityType,
  UserInviteRequest,
  UserInviteResponse,
  SelfSignUpRequest,
  SelfSignUpResponse,
  InvitationTrackingActionRequest,
  InvitationTrackingAction,
  InvitationTrackingActionResponse,
  TrackingReferenceStatusResponse,
  SignupEmailRequest,
  WorkflowMetadata,
  WorkflowAssociationType,
  PermissionDetails,
  WorkflowMetadataResponse,
  WorkflowMetadataPageResponse,
  WorkflowMetadataByOrganisationCodeResponse,
  CustomerPaginationOrderBy,
  orderBy__3,
  CustomerPaginationResponseItem,
  CustomerPaginationResponse,
  OrganisationCreateRequest,
  CreatePEPSanctionRequest,
  PermissionSummaryResponseFormat,
  format__3,
  OverrideEffect,
  PermissionCustomerSummaryV2,
  PermissionCustomerSummaryV2PageResponse,
  CreatePersonRequest,
  DocumentTypeChoiceResponse,
  IdentityDocumentRequirementV2Response,
  LivenessStatus,
  LivenessTransactionWithPresignedPortraitImageUrlResponse,
  LivenessTransactionWithPresignedVideoUrlResponse,
  UpdatePersonRequest,
  UpdateIdentityDocumentRequest,
  IdentityDocumentResponse,
  UpdateIdentityDocumentCaptureStatusRequest,
  IdentityDocumentFileItemResponse,
  IdentityDocumentUploadPresignResponse,
  ScanIdentityDocumentResultResponse,
  RunAssessmentRequest,
  AssessmentStatus,
  RuleStatus,
  MitigationTemplateStatus,
  RiskMitigationTemplateV2,
  RuleCriteriaComparison,
  RuleCriteriaV2,
  RiskAssessmentRuleV2,
  MitigationStatus,
  RiskMitigationCommentV2,
  RiskMitigationV2,
  RiskAssessmentV2,
  RiskAssessmentV2PageResponse,
  BlockerCode,
  Blocker,
  EligibilityResponse,
  RiskAssessmentSummaryV2,
  RiskAssessmentSummaryV2PageResponse,
  MitigationCommentRequest,
  MitigationOutcome,
  MitigationStatusRequest,
  OverrideStatus,
  RiskAssessmentOverrideResponse,
  CreateRiskAssessmentOverrideRequest,
};

const endpoints = makeApi([
  {
    method: 'post',
    path: '/internal/v1/customer/:customerId/organisation/:orgId/annul',
    alias: 'postInternalv1customerCustomerIdorganisationOrgIdannul',
    description: `Admin-only system-user trigger for the client record annulment. A call with dryRun&#x3D;true reads the pair&#x27;s rows and answers the plan, with no write. Any other call answers HTTP 501: the annulment itself is not built yet.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ClientRecordAnnulmentRequest,
      },
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'orgId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: ClientRecordAnnulmentResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Caller is not the system user with the admin role`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `Refused: a precondition failed. The refusals name the reason codes; the plan lists what was read`,
        schema: ClientRecordAnnulmentResponse,
      },
      {
        status: 501,
        description: `Client record annulment is not implemented yet`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/internal/v1/customer/:id/redact',
    alias: 'postInternalv1customerIdredact',
    description: `Admin-only system-user trigger that flips redaction columns synchronously and dispatches asynchronous archive work. Always returns HTTP 200; the body discriminates between success, idempotent re-issue, and precondition failure (per the always-200 wire convention).`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ reason: z.string() }).passthrough(),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: CustomerRedactionResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/internal/v1/integration/:clientId/deactivate',
    alias: 'patchInternalv1integrationClientIddeactivate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'clientId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Integration client not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/internal/v1/integration/register',
    alias: 'postInternalv1integrationregister',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: IntegrationRegistrationRequest,
      },
    ],
    response: IntegrationRegistrationResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Organisation not found`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `Active integration already exists for this organisation and partner type`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/internal/v1/user/:username/credential-status',
    alias: 'patchInternalv1userUsernamecredentialStatus',
    description: `Sets with_credentials&#x3D;true for the given username (Cognito sub). Setting false is not allowed (406). Returns 404 if the user does not exist.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ withCredentials: z.boolean() }).partial().passthrough(),
      },
      {
        name: 'username',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Missing or malformed request body.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `User does not exist.`,
        schema: z.void(),
      },
      {
        status: 406,
        description: `Setting the flag to false is not allowed.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/adversemedia/customer/:customerId',
    alias: 'getV1adversemediacustomerCustomerId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.array(AdverseMedia),
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the client or admin role to access this endpoint, further more if the user is a client user, the target customer must have permissioned the client organisation of the user.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The customer specified by customerId was not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/adversemedia/customer/:customerId',
    alias: 'postV1adversemediacustomerCustomerId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateAdverseMediaRequest,
      },
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: AdverseMedia,
    errors: [
      {
        status: 400,
        description: `The request was invalid.`,
        schema: z.array(ValidationResult),
      },
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the admin role to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The customer specified by customerId was not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/adversemedia/customer/:customerId/latest',
    alias: 'getV1adversemediacustomerCustomerIdlatest',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: AdverseMedia.nullable(),
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the client or admin role to access this endpoint, further more if the user is a client user, the target customer must have permissioned the client organisation of the user.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The customer specified by customerId was not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/company',
    alias: 'postV1company',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateCompanyRequest,
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/company/:id',
    alias: 'putV1companyId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateCompanyRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/company/:id',
    alias: 'getV1companyId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: Company,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/customer',
    alias: 'getV1customer',
    description: `List of Customers with a specific status`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'entityStatus',
        type: 'Query',
        schema: z.enum(['Draft', 'InProgress', 'Rejected', 'Verified']),
      },
      {
        name: 'entityType',
        type: 'Query',
        schema: z.enum([
          'Individual_Domestic',
          'Individual_Foreign',
          'SMSF',
          'Sole_Trader',
          'Partnership',
          'Private_Company',
          'Private_Foreign_Company',
          'Public_Company',
          'Public_Foreign_Company',
          'Trust_Regulated',
          'Trust_Unregulated',
          'Charity_Association',
        ]),
      },
      {
        name: 'resolveReferences',
        type: 'Query',
        schema: z.boolean().optional().default(true),
      },
    ],
    response: CustomerListResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/v1/customer/:customerId/user/roles/reviewer',
    alias: 'patchV1customerCustomerIduserrolesreviewer',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SyncReviewersRequest,
      },
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.array(ReviewerUserCustomerRole),
    errors: [
      {
        status: 400,
        description: `The request is invalid due to missing or incorrect parameters.`,
        schema: ValidationFailedResponse,
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 405,
        description: `The customer identity is verified and cannot be reviewed, or the customer identity is an individual or sole trader type that does not allow additional reviewers.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/customer/:customerId/user/roles/reviewer',
    alias: 'getV1customerCustomerIduserrolesreviewer',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.array(ReviewerUserCustomerRole),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/customer/:customerId/user/roles/reviewer',
    alias: 'postV1customerCustomerIduserrolesreviewer',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ReviewerUserCustomerRole,
      },
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.array(ReviewerUserCustomerRole),
    errors: [
      {
        status: 400,
        description: `The request is invalid due to missing or incorrect parameters.`,
        schema: ValidationFailedResponse,
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 405,
        description: `The customer identity is verified and cannot be reviewed, or the customer identity is an individual or sole trader type that does not allow additional reviewers.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/v1/customer/:customerId/user/roles/wallet',
    alias: 'patchV1customerCustomerIduserroleswallet',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SyncWalletHoldersRequest,
      },
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.array(WalletHolderUserCustomerRole),
    errors: [
      {
        status: 400,
        description: `The request is invalid due to missing or incorrect parameters.`,
        schema: ValidationFailedResponse,
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 405,
        description: `the customer identity is an individual or sole trader type that does not allow additional reviewers.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/customer/:customerId/user/roles/wallet',
    alias: 'getV1customerCustomerIduserroleswallet',
    description: `Find all users who will hold the specified identity in their account after the identity has been verified. A user can hold an identity in their account if they are a controller/viewer the of the specified customer. The actual role is derived from customer reference and assigned by the system or assigned directly by the administrator.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.array(WalletHolderUserCustomerRole),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/customer/:customerId/user/roles/wallet',
    alias: 'postV1customerCustomerIduserroleswallet',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: WalletHolderUserCustomerRole,
      },
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.array(WalletHolderUserCustomerRole),
    errors: [
      {
        status: 400,
        description: `The request is invalid due to missing or incorrect parameters.`,
        schema: ValidationFailedResponse,
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 405,
        description: `the customer identity is an individual or sole trader type that does not allow additional reviewers.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/customer/:customerId/userRole',
    alias: 'getV1customerCustomerIduserRole',
    description: `    List of all the users with the specified customer in their wallet and the roles they have in
    relation to the customer.
`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'includeCustomerReference',
        type: 'Query',
        schema: z.boolean().optional().default(false),
      },
    ],
    response: z.array(UserCustomerRoleResponse),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/customer/:id',
    alias: 'getV1customerId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: Customer,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v1/customer/:id',
    alias: 'deleteV1customerId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/customer/:id/byAdmin',
    alias: 'putV1customerIdbyAdmin',
    description: `The admin can make various different changes to the customer. The admin can also choose to send an
entityStatus change at the same time, or they can choose to update just the entity status
at a later time using the updateEntityStatus() method below.
`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `The update customer request`,
        type: 'Body',
        schema: CustomerRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v1/customer/:id/check',
    alias: 'deleteV1customerIdcheck',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/customer/:id/entityStatus',
    alias: 'putV1customerIdentityStatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateEntityStatusRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/customer/:id/identityDocumentStatus',
    alias: 'putV1customerIdidentityDocumentStatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateIdentityDocumentStatusRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v1/customer/:id/user',
    alias: 'deleteV1customerIduser',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'email',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'sources',
        type: 'Query',
        schema: z.array(z.any()).optional(),
      },
      {
        name: 'username',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `The request is invalid due to missing or incorrect parameters.`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/customer/:id/user',
    alias: 'postV1customerIduser',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CustomerAddUserRoleRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `The request is invalid due to missing or incorrect parameters.`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/customer/:id/verifiOnce/identityPageEligibility',
    alias: 'getV1customerIdverifiOnceidentityPageEligibility',
    description: `Temporary capability for the VerifiOnce (VO) collaboration: reports whether the
given customer should be shown the new identity page. A customer is eligible only
when they are an Individual (Domestic) entity with at least one active permission
that resolves, through its organisation, to at least one active organisation code
with vo_enabled set.

Only the customer&#x27;s own Controller (owner) may call this for their own id, not the
client or admin roles, and not another customer: VO enablement can reveal which
organisations a customer is permissioned with, so access is tighter than the plain
customer read (GET /v1/customer/{id}). This endpoint, and the identity page it
supports, will be removed once the VO collaboration ends.
`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.object({ eligible: z.boolean() }).passthrough(),
    errors: [
      {
        status: 401,
        description: `Unauthenticated, empty body, when no bearer token or an invalid one is presented.

An additional sign-in verification step may also be required before this endpoint
will answer, in which case the body is {&quot;error&quot;: &quot;STEP_UP_REQUIRED. &quot;} instead.
`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Forbidden. Empty body when the caller does not have the customer role (this
endpoint allows only customer, narrower than GET /v1/customer/{id}&#x27;s customer,
client and admin).

Otherwise, for a caller who does have the customer role, a JSON body
{&quot;error&quot;: &quot;User not authorised to read customer &lt;id&gt;. &quot;} when that caller is not
the Controller (owner) of the given id. This is returned identically whether the
id belongs to another, real customer or does not exist at all, so this endpoint
never reveals whether a well-formed id actually exists to anyone but its owner.
`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The id path segment is not a syntactically valid UUID, so no resource method could
be matched at all; JSON body
{&quot;error&quot;: &quot;HTTP 404 Not Found. Invalid UUID string: &lt;value&gt;. &quot;}. A well-formed but
unknown id is refused with 403 (above), never this status, so a malformed id is
the only way this endpoint returns 404.
`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/customer/byAdmin',
    alias: 'postV1customerbyAdmin',
    description: `The admin can create all types of customer, but must supply the email address of an existing user.
At the moment we do not support Admins creating customers for users that don&#x27;t exist. The admin
can also choose to send an entityStatus change at the same time, or they can choose to update
just the entity status at a later time using the updateEntityStatus() method below.
`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `The create customer request`,
        type: 'Body',
        schema: CustomerRequest,
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/customer/me',
    alias: 'getV1customerme',
    requestFormat: 'json',
    parameters: [
      {
        name: 'recursive',
        type: 'Query',
        schema: z.boolean().optional().default(true),
      },
    ],
    response: CustomerListResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/customer/user',
    alias: 'getV1customeruser',
    description: `    List of all the customers in the wallet of the specified user.
`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'email',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'recursive',
        type: 'Query',
        schema: z.boolean().optional().default(true),
      },
    ],
    response: CustomerListResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/document/scan/result/customer/:customerId/identityDocument',
    alias: 'getV1documentscanresultcustomerCustomerIdidentityDocument',
    description: `Find identity document scan results for the customer specified by id.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'documentType',
        type: 'Query',
        schema: z
          .enum(['DriverLicence', 'MedicareCard', 'Passport', 'PhotoCard', 'ImmiCard'])
          .optional(),
      },
      {
        name: 'latestOnly',
        type: 'Query',
        schema: z.boolean().optional().default(true),
      },
    ],
    response: z.array(IdentityDocumentScanResult),
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the admin role to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Specified customer not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/document/scan/result/customer/:customerId/identityDocument/:documentType/scan',
    alias: 'getV1documentscanresultcustomerCustomerIdidentityDocumentDocumentTypescan',
    description: `Manually trigger identity document scanning for the customer specified by id and identity document type.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'documentType',
        type: 'Path',
        schema: z.enum(['DriverLicence', 'MedicareCard', 'Passport', 'PhotoCard', 'ImmiCard']),
      },
    ],
    response: ScanDocumentApiResponse,
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the admin role to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Specified customer or document not found OR otherIdentityDocument type has been specified.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/document/scan/result/person/:id/identityDocument/:type/scan/threshold',
    alias: 'getV1documentscanresultpersonIdidentityDocumentTypescanthreshold',
    description: `This endpoint evaluates the scan threshold for a customer&#x27;s identity document. It is intended for internal use by the VerifiMe team to assess the effectiveness of scan algorithms. The results include similarity thresholds for full name, first name, and family name comparisons.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'type',
        type: 'Path',
        schema: z.enum(['Passport', 'DriverLicence']),
      },
    ],
    response: ScanThresholdResult.nullable(),
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the admin role to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Specified customer or document not found.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/document/scan/result/person/:id/identityDocument/MedicareCard/scan/threshold',
    alias: 'getV1documentscanresultpersonIdidentityDocumentMedicareCardscanthreshold',
    description: `This endpoint evaluates the scan threshold for a customer&#x27;s medicare card. It is intended for internal use by the VerifiMe team to assess the effectiveness of scan algorithms. The results include similarity thresholds for full name, first name, and family name comparisons.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.array(MedicareCardScanThresholdResult),
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the admin role to access this endpoint.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/feature/toggles',
    alias: 'getV1featuretoggles',
    description: `Returns the current status of all available feature toggles in the system`,
    requestFormat: 'json',
    response: z.array(FeatureToggleStatus),
    errors: [
      {
        status: 401,
        description: `Authentication required - user not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Access denied - admin role required`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/file',
    alias: 'postV1file',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: postV1file_Body,
      },
    ],
    response: FileUploadResponse,
    errors: [
      {
        status: 400,
        description: `The entity id, document type or file type is missing, the file type is not one of jpg, jpeg, png or pdf, or the document type is an identity document type that requires opt-in and an organisation code the customer onboards through has not opted in to it (or the customer onboards through no code). The opt-in refusal carries the errorCode DOCUMENT_TYPE_NOT_OPTED_IN in the body, and nothing is stored. A customer who already holds the document keeps it, but only an admin may add a file to it.`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/file/:entityId/:docType',
    alias: 'getV1fileEntityIdDocType',
    requestFormat: 'json',
    parameters: [
      {
        name: 'docType',
        type: 'Path',
        schema: z.enum([
          'DriverLicence',
          'MedicareCard',
          'OtherIdentityDocument',
          'Passport',
          'CompanyExtract',
          'PartnershipAgreement',
          'SoleTraderSupportDocument',
          'TrustDeed',
          'WholesaleCertificate',
          'RealEstateSupportDocument',
          'OtherSupportDocument',
          'PhotoCard',
          'ImmiCard',
        ]),
      },
      {
        name: 'entityId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: FileItemPageResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v1/file/:entityId/:docType/:fileId',
    alias: 'deleteV1fileEntityIdDocTypeFileId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'docType',
        type: 'Path',
        schema: z.enum([
          'DriverLicence',
          'MedicareCard',
          'OtherIdentityDocument',
          'Passport',
          'CompanyExtract',
          'PartnershipAgreement',
          'SoleTraderSupportDocument',
          'TrustDeed',
          'WholesaleCertificate',
          'RealEstateSupportDocument',
          'OtherSupportDocument',
          'PhotoCard',
          'ImmiCard',
        ]),
      },
      {
        name: 'entityId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'fileId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/file/:entityId/:docType/:fileId/presignDownload',
    alias: 'getV1fileEntityIdDocTypeFileIdpresignDownload',
    requestFormat: 'json',
    parameters: [
      {
        name: 'docType',
        type: 'Path',
        schema: z.enum([
          'DriverLicence',
          'MedicareCard',
          'OtherIdentityDocument',
          'Passport',
          'CompanyExtract',
          'PartnershipAgreement',
          'SoleTraderSupportDocument',
          'TrustDeed',
          'WholesaleCertificate',
          'RealEstateSupportDocument',
          'OtherSupportDocument',
          'PhotoCard',
          'ImmiCard',
        ]),
      },
      {
        name: 'entityId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'fileId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: PresignUrl,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/file/presign',
    alias: 'postV1filepresign',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PresignApiRequest,
      },
    ],
    response: PresignResponse,
    errors: [
      {
        status: 400,
        description: `A file name does not end in an allowed file type (pdf, jpg, png or jpeg), or the file type is an identity document type that requires opt-in and an organisation code the customer onboards through has not opted in to it (or the customer onboards through no code). The opt-in refusal carries the errorCode DOCUMENT_TYPE_NOT_OPTED_IN in the body, and no URL is issued. A customer who already holds the document keeps it, but only an admin may add a file to it.`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/logo/organisationCode/:organisationCode',
    alias: 'postV1logoorganisationCodeOrganisationCode',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: postV1logoorganisationCodeOrganisationCode_Body,
      },
      {
        name: 'organisationCode',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.object({ url: z.string() }).partial().passthrough(),
    errors: [
      {
        status: 400,
        description: `Validation failed`,
        schema: ValidationFailedResponse,
      },
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Only admin or client users are allowed to upload logo for organisation.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/logo/organisationCode/:organisationCode',
    alias: 'getV1logoorganisationCodeOrganisationCode',
    requestFormat: 'json',
    parameters: [
      {
        name: 'organisationCode',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: LogoResponse,
    errors: [
      {
        status: 404,
        description: `No logo found with the given organisation code`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/logo/tracking/:trackingReference',
    alias: 'getV1logotrackingTrackingReference',
    requestFormat: 'json',
    parameters: [
      {
        name: 'trackingReference',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: LogoResponse,
    errors: [
      {
        status: 404,
        description: `No logo found with the given tracking id`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/meta',
    alias: 'getV1meta',
    requestFormat: 'json',
    response: z.record(MetaEntity),
  },
  {
    method: 'get',
    path: '/v1/meta/:name',
    alias: 'getV1metaName',
    requestFormat: 'json',
    parameters: [
      {
        name: 'name',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: MetaEntity,
  },
  {
    method: 'get',
    path: '/v1/meta/error',
    alias: 'getV1metaerror',
    requestFormat: 'json',
    response: z.record(z.record(z.string())),
  },
  {
    method: 'get',
    path: '/v1/organisation',
    alias: 'getV1organisation',
    description: `Find all organisations based on the query parameters. The query parameters are used to filter, sort and paginate the results.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'includeInactive',
        type: 'Query',
        schema: z.boolean().optional().default(false),
      },
      {
        name: 'lastIndex',
        type: 'Query',
        schema: z.number().int().optional().default(24),
      },
      {
        name: 'level',
        type: 'Query',
        schema: z.enum(['All', 'ChildOnly', 'ParentOnly']).optional().default('All'),
      },
      {
        name: 'orderBy',
        type: 'Query',
        schema: z.literal('organisationName').optional().default('organisationName'),
      },
      {
        name: 'orderDirection',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional().default('asc'),
      },
      {
        name: 'startIndex',
        type: 'Query',
        schema: z.number().int().optional().default(0),
      },
    ],
    response: OrganisationPageResponse,
    errors: [
      {
        status: 401,
        description: `Invalid or missing authentication`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/organisation',
    alias: 'postV1organisation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: Organisation,
      },
    ],
    response: OrganisationResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Bearer token is not specified or incorrect`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Admin role required`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/organisation/:id',
    alias: 'putV1organisationId',
    description: `Update an existing organisation. The id in the path must match the id in the organisation object. If the organisation is not found, a 404 response code is returned.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: Organisation,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: OrganisationResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request, such as missing organisation id`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Invalid or missing authentication`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Insufficient permissions`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Organisation not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/organisation/:id',
    alias: 'getV1organisationId',
    description: `Find an organisation by its unique identifier. If the organisation is not found, a 404 response code is returned.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: Organisation,
    errors: [
      {
        status: 401,
        description: `Invalid or missing authentication`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Organisation not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/v1/organisation/:id/deactivate',
    alias: 'patchV1organisationIddeactivate',
    description: `Deactivate an organisation specified by the id. This operation is idempotent, if the organisation is already inactive, no action will be taken.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Invalid request, such as missing organisation id`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Invalid or missing authentication`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Insufficient permissions`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Organisation not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v1/organisation/:id/user',
    alias: 'deleteV1organisationIduser',
    description: `Remove a user to the organisation specified by the id. Please note, this operation is idempotent, if the user does not belong to the organisation, no action will be taken.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ username: z.string() }).passthrough(),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Invalid request, such as missing organisation id or username`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Missing or invalid authentication`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Insufficient permissions`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/organisation/:id/user',
    alias: 'getV1organisationIduser',
    description: `List users that belong to the organisation specified by the id. Please note that if the organisation does not exist, an empty list will be returned, we don&#x27;t validate the existence of the organisation.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.array(UserSummary),
    errors: [
      {
        status: 401,
        description: `Missing or invalid authentication`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Insufficient permissions`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Invalid organisation id format`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/organisation/:id/user',
    alias: 'postV1organisationIduser',
    description: `Add a user to the organisation specified by the id. The user must already exist in the system.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ username: z.string() }).passthrough(),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Invalid request, such as missing organisation id or username`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Missing or invalid authentication`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Insufficient permissions`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/v1/organisation/:ofChildEntityId/parent/:toParentEntityId',
    alias: 'patchV1organisationOfChildEntityIdparentToParentEntityId',
    description: `Update the parent of a child organisation. Both the child and parent organisations must exist, and the child organisation must not be a parent of another organisation. This operation is used to manage the hierarchy of organisations.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'ofChildEntityId',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'toParentEntityId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OrganisationResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request, such as missing organisation ids`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Invalid or missing authentication`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Insufficient permissions`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Child or parent organisation not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/organisation/:organisationId/code',
    alias: 'getV1organisationOrganisationIdcode',
    requestFormat: 'json',
    parameters: [
      {
        name: 'organisationId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'includeInactive',
        type: 'Query',
        schema: z.boolean().optional().default(false),
      },
    ],
    response: z.array(OrganisationCode),
    errors: [
      {
        status: 401,
        description: `User is not authenticated.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to view codes for this organisation.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/organisation/:organisationId/code',
    alias: 'postV1organisationOrganisationIdcode',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateOrganisationCodeRequest,
      },
      {
        name: 'organisationId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: OrganisationCode,
    errors: [
      {
        status: 400,
        description: `Validation error or mismatch between path and body IDs.`,
        schema: ValidationFailedResponse,
      },
      {
        status: 401,
        description: `User is not authenticated.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to create a code for this organisation.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/organisation/:organisationId/notification/preferences',
    alias: 'getV1organisationOrganisationIdnotificationpreferences',
    requestFormat: 'json',
    parameters: [
      {
        name: 'organisationId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'status',
        type: 'Query',
        schema: z.enum(['ACTIVE', 'INACTIVE']).optional(),
      },
      {
        name: 'type',
        type: 'Query',
        schema: z.enum(['EMAIL', 'WEBHOOK']).optional(),
      },
    ],
    response: z.array(OrganisationNotificationPreferenceResponse),
    errors: [
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not allowed read the organisation notification preferences`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/organisation/:organisationId/notification/preferences/type/:type',
    alias: 'postV1organisationOrganisationIdnotificationpreferencestypeType',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `The notification preference details to create`,
        type: 'Body',
        schema: z.object({ detail: z.string() }).passthrough(),
      },
      {
        name: 'organisationId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'type',
        type: 'Path',
        schema: z.enum(['EMAIL', 'WEBHOOK']),
      },
    ],
    response: OrganisationNotificationPreferenceResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request body`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not allowed read the organisation notification preferences`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/organisation/code/:organisationCode',
    alias: 'getV1organisationcodeOrganisationCode',
    requestFormat: 'json',
    parameters: [
      {
        name: 'organisationCode',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OrganisationCode,
    errors: [
      {
        status: 401,
        description: `User is not authenticated.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to view this organisation code.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The specified organisation code was not found.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/v1/organisation/code/:organisationCode/deactivate',
    alias: 'patchV1organisationcodeOrganisationCodedeactivate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'organisationCode',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Code must be supplied.`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User is not authenticated.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to perform this action.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The specified organisation code was not found.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/v1/organisation/code/:organisationCode/individualOnly',
    alias: 'patchV1organisationcodeOrganisationCodeindividualOnly',
    requestFormat: 'json',
    parameters: [
      {
        name: 'organisationCode',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OrganisationCode,
    errors: [
      {
        status: 401,
        description: `User is not authenticated.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to perform this action.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The specified organisation code was not found.`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `The requested state transition is not allowed.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/organisation/code/:organisationCode/optedInDocumentTypes',
    alias: 'putV1organisationcodeOrganisationCodeoptedInDocumentTypes',
    description: `Replace-all semantics. Only a VerifiMe admin may call this. Each entry must be an identity document type that requires opt-in. Null or empty clears the list.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateOptedInDocumentTypesRequest,
      },
      {
        name: 'organisationCode',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OrganisationCode,
    errors: [
      {
        status: 400,
        description: `One or more entries are not identity document types that require opt-in.`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User is not authenticated.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not a VerifiMe admin.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The specified organisation code was not found.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/organisation/code/:organisationCode/redirectAllowedOrigins',
    alias: 'putV1organisationcodeOrganisationCoderedirectAllowedOrigins',
    description: `Replace-all semantics. Each entry must be an absolute HTTPS origin (scheme, host, optional port) with no path, query, or fragment. Null or empty clears the list.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateRedirectAllowedOriginsRequest,
      },
      {
        name: 'organisationCode',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OrganisationCode,
    errors: [
      {
        status: 400,
        description: `One or more origins are invalid, or the allow-list exceeds the storage limit.`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User is not authenticated.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to perform this action.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The specified organisation code was not found.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/organisation/code/:organisationCode/returnUrl',
    alias: 'putV1organisationcodeOrganisationCodereturnUrl',
    description: `Updates the return URL for the specified organisation code. The return URL is where users should be redirected after completing verification. Setting returnUrl to null or empty string clears thereturn URL configuration, any other value is treated as a URL.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ returnUrl: z.string().url().nullable() }).partial().passthrough(),
      },
      {
        name: 'organisationCode',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OrganisationCode,
    errors: [
      {
        status: 400,
        description: `Invalid request body (e.g., invalid URL format).`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User is not authenticated.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to perform this action.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The specified organisation code was not found.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/organisation/code/:organisationCode/voEnabled',
    alias: 'putV1organisationcodeOrganisationCodevoEnabled',
    description: `When VO is enabled, a partner also issues a verifiable credential for customers onboarded under this code and sends one combined VerifiMe branded email, so person-api suppresses its own verified email for them.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ voEnabled: z.boolean() }).passthrough(),
      },
      {
        name: 'organisationCode',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OrganisationCode,
    errors: [
      {
        status: 400,
        description: `Invalid request body (e.g., null voEnabled).`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User is not authenticated.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to perform this action.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The specified organisation code was not found.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/organisation/code/:organisationCode/workflow',
    alias: 'putV1organisationcodeOrganisationCodeworkflow',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateWorkflowTypeRequest,
      },
      {
        name: 'organisationCode',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: OrganisationCode,
    errors: [
      {
        status: 400,
        description: `Invalid request body (e.g., null workflowType).`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User is not authenticated.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to perform this action.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The specified organisation code was not found.`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `The requested state transition is not allowed (e.g., from INDIVIDUAL_ONLY to ORGANISATION_ONLY).`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/organisation/file/:entityId/export',
    alias: 'getV1organisationfileEntityIdexport',
    requestFormat: 'json',
    parameters: [
      {
        name: 'entityId',
        type: 'Path',
        schema: z.string().regex(/^(PE|SE)-[0-9]{5}$/),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/organisation/file/import',
    alias: 'postV1organisationfileimport',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z
          .object({ file: z.instanceof(File) })
          .partial()
          .passthrough(),
      },
    ],
    response: OrganisationResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/organisation/me',
    alias: 'getV1organisationme',
    description: `Find organisations that the current user has been provisioned to, only active ones by default.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'includeInactive',
        type: 'Query',
        schema: z.boolean().optional().default(false),
      },
    ],
    response: z.array(Organisation),
    errors: [
      {
        status: 401,
        description: `Invalid or missing authentication`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Client role required`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/organisation/me/code',
    alias: 'getV1organisationmecode',
    requestFormat: 'json',
    parameters: [
      {
        name: 'includeInactive',
        type: 'Query',
        schema: z.boolean().optional().default(false),
      },
    ],
    response: z.array(OrganisationCode),
    errors: [
      {
        status: 401,
        description: `User is not authenticated.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to view codes for their organisations.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/organisation/notification/preferences/:preferenceId',
    alias: 'putV1organisationnotificationpreferencesPreferenceId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `The updated notification preference details including version for optimistic locking`,
        type: 'Body',
        schema: UpdateOrganisationNotificationPreferenceRequest,
      },
      {
        name: 'preferenceId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: OrganisationNotificationPreferenceResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request body`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not allowed update the organisation notification preferences`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Notification preference not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/v1/organisation/notification/preferences/:preferenceId/deactivate',
    alias: 'patchV1organisationnotificationpreferencesPreferenceIddeactivate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'preferenceId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: OrganisationNotificationPreferenceResponse,
    errors: [
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not allowed update the organisation notification preferences`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Notification preference not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/organisation/summary',
    alias: 'getV1organisationsummary',
    description: `Find organisation summaries based on the criteria for the organisation.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'criteria',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'includeInactive',
        type: 'Query',
        schema: z.boolean().optional().default(false),
      },
    ],
    response: z.array(OrgSummarySearchResult),
    errors: [
      {
        status: 401,
        description: `Invalid or missing authentication`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/organisation/summary/customer/:customerId',
    alias: 'getV1organisationsummarycustomerCustomerId',
    description: `Find organisation summaries based on the criteria for the organisation, excluding those that the customer has already permission to. This prevents the customer to see the ones they  cannot permission again.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'criteria',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'ignoreStatus',
        type: 'Query',
        schema: z.boolean().optional().default(false),
      },
      {
        name: 'includeInactive',
        type: 'Query',
        schema: z.boolean().optional().default(false),
      },
    ],
    response: z.array(OrgSummarySearchResult),
    errors: [
      {
        status: 401,
        description: `Invalid or missing authentication`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/partnership',
    alias: 'postV1partnership',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreatePartnershipRequest,
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/partnership/:id',
    alias: 'putV1partnershipId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdatePartnershipRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/pepsanction/customer/:customerId',
    alias: 'getV1pepsanctioncustomerCustomerId',
    description: `Use the /v2/pepsanction/customer/{customerId} endpoint instead`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: PEPSanctionPageResponse,
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the client or admin role to access this endpoint, further more if the user is a client user, the target customer must have permissioned the client organisation of the user.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The customer specified by customerId was not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/pepsanction/customer/:customerId',
    alias: 'postV1pepsanctioncustomerCustomerId',
    description: `Use the /v2/pepsanction/customer/{customerId} endpoint instead`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PEPSanction,
      },
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: PEPSanctionResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the admin role to access this endpoint.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/pepsanction/customer/:customerId/latest',
    alias: 'getV1pepsanctioncustomerCustomerIdlatest',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: PEPSanction,
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the client or admin role to access this endpoint, further more if the user is a client user, the target customer must have permissioned the client organisation of the user.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The customer specified by customerId was not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/permission',
    alias: 'getV1permission',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Query',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid()
          .optional(),
      },
    ],
    response: z.array(Permission),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/permission',
    alias: 'postV1permission',
    description: `This method adds a new permission. The caller must have the customer or admin role to execute this operation.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `Details of the permission to be added. Please note, controllerId will be set automatically by the system.`,
        type: 'Body',
        schema: CreatePermissionRequest,
      },
    ],
    response: PermissionResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `The user is not authenticated.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `The caller does not have permission to execute this operation`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `The customer, or for a company or other entity one of its controllers, holds a document of a type that requires opt-in (a Rejected document is not held), and the granted organisation&#x27;s organisation code, or another code the customer onboards through, has not opted in to that type. Admin grants are refused too. An organisation that already holds an active permission for the customer is not judged again. Nothing is granted. The body carries the errorCode HELD_DOCUMENT_TYPE_NOT_ACCEPTED.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v1/permission/:id',
    alias: 'deleteV1permissionId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/permission/organisation/:orgId',
    alias: 'getV1permissionorganisationOrgId',
    description: `This method finds a list of Permissions for a given CLIENT. The caller must have the admin or client role to execute this query.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'orgId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'entityType',
        type: 'Query',
        schema: z
          .enum([
            'Individual_Domestic',
            'Individual_Foreign',
            'SMSF',
            'Sole_Trader',
            'Partnership',
            'Private_Company',
            'Private_Foreign_Company',
            'Public_Company',
            'Public_Foreign_Company',
            'Trust_Regulated',
            'Trust_Unregulated',
            'Charity_Association',
          ])
          .optional(),
      },
      {
        name: 'format',
        type: 'Query',
        schema: format,
      },
      {
        name: 'fromDate',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'lastAssessedFromDate',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'lastAssessedToDate',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'lastIndex',
        type: 'Query',
        schema: z.number().int().optional().default(24),
      },
      {
        name: 'name',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'orderBy',
        type: 'Query',
        schema: orderBy,
      },
      {
        name: 'orderDirection',
        type: 'Query',
        schema: orderDirection,
      },
      {
        name: 'permissionFromDate',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'permissionToDate',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'referenceData',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'riskLevel',
        type: 'Query',
        schema: z.enum(['Pending', 'Low', 'Medium', 'High', 'Extreme']).optional(),
      },
      {
        name: 'startIndex',
        type: 'Query',
        schema: z.number().int().optional().default(0),
      },
      {
        name: 'status',
        type: 'Query',
        schema: z.string().optional().default('Active'),
      },
      {
        name: 'statuses',
        type: 'Query',
        schema: z.array(PermissionStatus).optional(),
      },
      {
        name: 'toDate',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'wholesaleCertificateStatus',
        type: 'Query',
        schema: z.enum(['Draft', 'InProgress', 'Rejected', 'Verified']).optional(),
      },
      {
        name: 'xlsxTimeZoneId',
        type: 'Query',
        schema: z.string().optional().default('Australia/Sydney'),
      },
    ],
    response: PermissionCustomerSummaryPageResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `The caller does not have permission to execute this query`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/permission/request',
    alias: 'getV1permissionrequest',
    requestFormat: 'json',
    parameters: [
      {
        name: 'email',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'userId',
        type: 'Query',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid()
          .optional(),
      },
      {
        name: 'username',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.array(PermissionRequestSummary),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/permission/request',
    alias: 'postV1permissionrequest',
    description: `Create a permission request for a user to permission an organisation.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PermissionRequestRequest,
      },
    ],
    response: PermissionRequestResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v1/permission/request/:id',
    alias: 'deleteV1permissionrequestId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Permission request not found`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `An ask-first permission request that cannot be dismissed: it is closed (completed by a grant, or already dismissed), which carries no errorCode, or it is an entity request whose Individual request is granted, which carries the errorCode ENTITY_CHOICE_LOCKED in the body. Its completion time is unchanged. A Legacy request is never refused.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/permission/request/me',
    alias: 'getV1permissionrequestme',
    requestFormat: 'json',
    response: z.array(PermissionRequestSummary),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/person',
    alias: 'postV1person',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: Person,
      },
      {
        name: 'addToUser',
        type: 'Query',
        schema: z.boolean().optional(),
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/person/:customerId/identityDocument/:documentType/verification',
    alias: 'postV1personCustomerIdidentityDocumentDocumentTypeverification',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'documentType',
        type: 'Path',
        schema: z.enum([
          'Passport',
          'DriverLicence',
          'OtherIdentityDocument',
          'MedicareCard',
          'PhotoCard',
          'ImmiCard',
        ]),
      },
    ],
    response: IdentityDocumentVerificationResult,
    errors: [
      {
        status: 400,
        description: `Unsupported document type for DVS verification`,
        schema: ValidationFailedResponse,
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The customer with the given ID was not found`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `The customer already has a pending verification result for the given document type`,
        schema: IdentityDocumentVerificationResult,
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/person/:customerId/identityDocument/:documentType/verification',
    alias: 'getV1personCustomerIdidentityDocumentDocumentTypeverification',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'documentType',
        type: 'Path',
        schema: z.enum([
          'Passport',
          'DriverLicence',
          'OtherIdentityDocument',
          'MedicareCard',
          'PhotoCard',
          'ImmiCard',
        ]),
      },
    ],
    response: z.array(IdentityDocumentVerificationResult),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/person/:customerId/identityDocument/:documentType/verification/current',
    alias: 'getV1personCustomerIdidentityDocumentDocumentTypeverificationcurrent',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'documentType',
        type: 'Path',
        schema: z.enum([
          'Passport',
          'DriverLicence',
          'OtherIdentityDocument',
          'MedicareCard',
          'PhotoCard',
          'ImmiCard',
        ]),
      },
    ],
    response: IdentityDocumentVerificationResult.nullable(),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/person/:customerId/identityDocument/:documentType/verification/status',
    alias: 'putV1personCustomerIdidentityDocumentDocumentTypeverificationstatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `The status override request`,
        type: 'Body',
        schema: IdentityDocumentVerificationStatusOverrideRequest,
      },
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'documentType',
        type: 'Path',
        schema: z.enum([
          'Passport',
          'DriverLicence',
          'OtherIdentityDocument',
          'MedicareCard',
          'PhotoCard',
          'ImmiCard',
        ]),
      },
    ],
    response: IdentityDocumentVerificationResult,
    errors: [
      {
        status: 400,
        description: `The request was invalid`,
        schema: ValidationFailedResponse,
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The customer with the given ID was not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/person/:customerId/liveness/transaction',
    alias: 'postV1personCustomerIdlivenesstransaction',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `The liveness check transaction to associate with the person`,
        type: 'Body',
        schema: LivenessCheckTransaction,
      },
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `The request was invalid`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the customer role and must be able to update the customer specified by customerId to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The liveness check transaction was not found`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `The liveness check transaction is associated with the wrong person`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/person/:customerId/organisation/:organisationId/verification/confidence',
    alias: 'getV1personCustomerIdorganisationOrganisationIdverificationconfidence',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'organisationId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: IdentityConfidenceResponse,
    errors: [
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to view detail of the customer`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `No customer found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/person/:customerId/organisation/:organisationId/verification/pipeline',
    alias: 'getV1personCustomerIdorganisationOrganisationIdverificationpipeline',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'organisationId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: VerificationPipelineResponse,
    errors: [
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to view detail of the customer`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `No customer found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/person/:customerId/overall/verification/pipeline',
    alias: 'getV1personCustomerIdoverallverificationpipeline',
    description: `Note: This is an admin only endpoint.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: VerificationPipelineResponse,
    errors: [
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to view detail of the customer`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `No customer found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/person/:customerId/portrait/comparison',
    alias: 'getV1personCustomerIdportraitcomparison',
    description: `DEPRECATED: Use v2 endpoint instead which returns null with 200 status code instead of 404 when a portrait comparison is not found.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: PortraitComparisonResponse,
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the admin or client role, and client must have the rights to read customer (i.e. permission granted to client.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `No portrait face comparison result for the specified customer.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/person/:customerId/verification/pipeline',
    alias: 'getV1personCustomerIdverificationpipeline',
    description: `Note: This is deprecated. Please use either /v1/person/{customerId}/organisation/{orgId}/verification/pipeline or /v1/person/{customerId}/overall/verification/pipeline`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: VerificationPipelineResponse,
    errors: [
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to view detail of the customer`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `No customer found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/person/:id',
    alias: 'putV1personId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: Person,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/person/:id',
    alias: 'getV1personId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: Person,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/person/:id/identityDocument',
    alias: 'putV1personIdidentityDocument',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: IdentityDocumentRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/person/:id/portrait',
    alias: 'postV1personIdportrait',
    description: `Manual update portrait urls for an individual`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdatePortraitRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: IdentityPortraitItemResponse,
    errors: [
      {
        status: 400,
        description: `FileId is not supplied`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to update portrait`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/person/:id/portrait',
    alias: 'getV1personIdportrait',
    description: `Get the latest portrait url of an individual, no matter what type is`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: IdentityPortraitItemResponse.nullable(),
    errors: [
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to view detail of the individual`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/person/:id/verificationDocuments',
    alias: 'putV1personIdverificationDocuments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.array(DocumentType),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/person/:id/verificationDocuments',
    alias: 'getV1personIdverificationDocuments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.array(FileItem),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/person/:id/wholesaleCertificate',
    alias: 'putV1personIdwholesaleCertificate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: WholesaleCertificateUpdateRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `The certificate references a fileId that no longer exists for the customer`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Wholesale certificate not found for the customer`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/person/:id/wholesaleCertificate',
    alias: 'postV1personIdwholesaleCertificate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ fileId: z.string() }).partial().passthrough(),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/person/:id/wholesaleCertificate/certificateStatus',
    alias: 'putV1personIdwholesaleCertificatecertificateStatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: WholesaleCertificateStatusRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request`,
        schema: ValidationFailedResponse,
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Wholesale certificate not found for the customer`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `Wholesale certificate is not in the InProgress status`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/person/identityDocument/verification/:verificationId',
    alias: 'getV1personidentityDocumentverificationVerificationId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'verificationId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: IdentityDocumentVerificationResult,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `No verification found with the given ID`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/person/identityDocument/verification/:verificationId/status',
    alias: 'putV1personidentityDocumentverificationVerificationIdstatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateVerificationResultRequest,
      },
      {
        name: 'verificationId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: IdentityDocumentVerificationResult,
    errors: [
      {
        status: 400,
        description: `The request was invalid`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `No verification found with the given ID`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `The status of the verification result cannot be updated to the requested status`,
        schema: IdentityDocumentVerificationResult,
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/person/new',
    alias: 'getV1personnew',
    requestFormat: 'json',
    response: Person,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/reference/authorizedIssuers',
    alias: 'getV1referenceauthorizedIssuers',
    requestFormat: 'json',
    response: z.array(AuthorizedIssuerReferenceResponse),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/reference/authorizedIssuers/search',
    alias: 'getV1referenceauthorizedIssuerssearch',
    requestFormat: 'json',
    parameters: [
      {
        name: 'term',
        type: 'Query',
        schema: z.string().optional().default(''),
      },
    ],
    response: z.array(AuthorizedIssuerReferenceResponse),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/reference/countries',
    alias: 'getV1referencecountries',
    requestFormat: 'json',
    response: z.array(CountryReferenceResponse),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/reference/countries/search',
    alias: 'getV1referencecountriessearch',
    requestFormat: 'json',
    parameters: [
      {
        name: 'term',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.array(CountryReferenceResponse),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/report/organisation/:organisationId/customer/:customerId/report-capability',
    alias: 'postV1reportorganisationOrganisationIdcustomerCustomerIdreportCapability',
    description: `Returns a download URL embedding a short-lived, KMS-signed capability JWT. The browser uses this URL to fetch the report artefact bytes directly from the Report Service.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'organisationId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.object({ downloadUrl: z.string() }).partial().passthrough(),
    errors: [
      {
        status: 401,
        description: `The user is not authenticated.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `The caller cannot read this customer.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The customer has no risk assessment under this organisation yet.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/report/organisation/:organisationId/customer/:customerId/report-requests',
    alias: 'postV1reportorganisationOrganisationIdcustomerCustomerIdreportRequests',
    description: `Idempotent: ensures the report artefact exists for the customer&#x27;s latest risk assessment and records a pending request. A separate poller notifies the requesting user by email when the artefact is ready; this endpoint does not itself emit any notification.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'organisationId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: ReportRequestResponse,
    errors: [
      {
        status: 401,
        description: `The user is not authenticated.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `The caller cannot read this customer.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The customer has no risk assessment under this organisation yet.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/risk/assessment',
    alias: 'postV1riskassessment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RiskAssessment,
      },
    ],
    response: RiskAssessmentResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/risk/assessment/:id',
    alias: 'putV1riskassessmentId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RiskAssessment,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskAssessmentResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/risk/assessment/:id',
    alias: 'getV1riskassessmentId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskAssessment,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v1/risk/assessment/:id',
    alias: 'deleteV1riskassessmentId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/risk/assessment/:riskAssessmentId/mitigation/:riskMitigationId/status/:status',
    alias: 'putV1riskassessmentRiskAssessmentIdmitigationRiskMitigationIdstatusStatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RiskMitigationComment,
      },
      {
        name: 'riskAssessmentId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'riskMitigationId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'status',
        type: 'Path',
        schema: z.enum(['Open', 'Pass', 'Fail', 'Ignore']),
      },
    ],
    response: RiskAssessment,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/risk/assessment/organisation/:orgId',
    alias: 'getV1riskassessmentorganisationOrgId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'orgId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'assessedRiskLevel',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'assessmentStatus',
        type: 'Query',
        schema: z.enum(['NoRisksDetected', 'Open', 'Closed']).optional(),
      },
      {
        name: 'currentRiskLevel',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'customerId',
        type: 'Query',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid()
          .optional(),
      },
      {
        name: 'customerName',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'entityType',
        type: 'Query',
        schema: z
          .enum([
            'Individual_Domestic',
            'Individual_Foreign',
            'SMSF',
            'Sole_Trader',
            'Partnership',
            'Private_Company',
            'Private_Foreign_Company',
            'Public_Company',
            'Public_Foreign_Company',
            'Trust_Regulated',
            'Trust_Unregulated',
            'Charity_Association',
          ])
          .optional(),
      },
      {
        name: 'fromDateCreated',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'fromLastUpdated',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'lastIndex',
        type: 'Query',
        schema: z.number().int().optional(),
      },
      {
        name: 'orderBy',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'orderDirection',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'startIndex',
        type: 'Query',
        schema: z.number().int().optional(),
      },
      {
        name: 'toDateCreated',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'toLastUpdated',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: RiskAssessmentPageResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/risk/assessment/rule',
    alias: 'postV1riskassessmentrule',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RiskAssessmentRule,
      },
    ],
    response: RiskAssessmentRuleResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/risk/assessment/rule/:id',
    alias: 'putV1riskassessmentruleId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RiskAssessmentRule,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskAssessmentRuleResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/risk/assessment/rule/:id',
    alias: 'getV1riskassessmentruleId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskAssessmentRule,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v1/risk/assessment/rule/:id',
    alias: 'deleteV1riskassessmentruleId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskAssessmentRuleResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v1/risk/assessment/rule/:id/check',
    alias: 'deleteV1riskassessmentruleIdcheck',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskAssessmentRuleResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/risk/assessment/rule/:id/status/:status',
    alias: 'putV1riskassessmentruleIdstatusStatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'status',
        type: 'Path',
        schema: z.enum(['Active', 'Inactive']),
      },
    ],
    response: RiskAssessmentRuleResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v1/risk/assessment/rule/:id/template',
    alias: 'deleteV1riskassessmentruleIdtemplate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RiskAssessmentRuleTemplateRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskAssessmentRuleResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/risk/assessment/rule/:id/template',
    alias: 'putV1riskassessmentruleIdtemplate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RiskAssessmentRuleTemplateRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskAssessmentRuleResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/risk/assessment/rule/organisation/:orgId',
    alias: 'getV1riskassessmentruleorganisationOrgId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'orgId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'ignoreStatus',
        type: 'Query',
        schema: z.boolean().optional().default(false),
      },
      {
        name: 'lastIndex',
        type: 'Query',
        schema: z.number().int().optional().default(24),
      },
      {
        name: 'startIndex',
        type: 'Query',
        schema: z.number().int().optional().default(0),
      },
    ],
    response: RiskAssessmentRulePageResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/risk/assessment/run',
    alias: 'postV1riskassessmentrun',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RiskAssessmentProcessRequest,
      },
    ],
    response: RiskAssessment,
    errors: [
      {
        status: 400,
        description: `Validation error`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 504,
        description: `The risk assessment run did not complete within the timeout; it may still finish server-side`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/risk/assessment/run/check',
    alias: 'postV1riskassessmentruncheck',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RiskAssessmentProcessRequest,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Process not allowed to run. The customer has not permissioned the client organisation.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/risk/assessment/summary/organisation/:orgId',
    alias: 'getV1riskassessmentsummaryorganisationOrgId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'orgId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'assessedRiskLevel',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'assessmentStatus',
        type: 'Query',
        schema: z.enum(['NoRisksDetected', 'Open', 'Closed']).optional(),
      },
      {
        name: 'currentRiskLevel',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'customerId',
        type: 'Query',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid()
          .optional(),
      },
      {
        name: 'customerName',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'entityType',
        type: 'Query',
        schema: z
          .enum([
            'Individual_Domestic',
            'Individual_Foreign',
            'SMSF',
            'Sole_Trader',
            'Partnership',
            'Private_Company',
            'Private_Foreign_Company',
            'Public_Company',
            'Public_Foreign_Company',
            'Trust_Regulated',
            'Trust_Unregulated',
            'Charity_Association',
          ])
          .optional(),
      },
      {
        name: 'fromDateCreated',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'fromLastUpdated',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'lastIndex',
        type: 'Query',
        schema: z.number().int().optional().default(24),
      },
      {
        name: 'orderBy',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'orderDirection',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'startIndex',
        type: 'Query',
        schema: z.number().int().optional().default(0),
      },
      {
        name: 'toDateCreated',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'toLastUpdated',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: PageResultRiskAssessmentSummary,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/risk/mitigation/:id',
    alias: 'putV1riskmitigationId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RiskMitigation,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskMitigationResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/risk/mitigation/:id',
    alias: 'getV1riskmitigationId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskMitigation,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/risk/mitigation/:id/comment',
    alias: 'putV1riskmitigationIdcomment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RiskMitigationComment,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskMitigation,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/risk/mitigation/:id/status/:status',
    alias: 'putV1riskmitigationIdstatusStatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RiskMitigationComment,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'status',
        type: 'Path',
        schema: z.enum(['Open', 'Pass', 'Fail', 'Ignore']),
      },
    ],
    response: RiskMitigation,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/risk/mitigation/template',
    alias: 'postV1riskmitigationtemplate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RiskMitigationTemplate,
      },
    ],
    response: RiskMitigationTemplateResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/risk/mitigation/template/:id',
    alias: 'putV1riskmitigationtemplateId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RiskMitigationTemplate,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskMitigationTemplateResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/risk/mitigation/template/:id',
    alias: 'getV1riskmitigationtemplateId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskMitigationTemplate,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v1/risk/mitigation/template/:id',
    alias: 'deleteV1riskmitigationtemplateId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskMitigationTemplateResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v1/risk/mitigation/template/:id/check',
    alias: 'deleteV1riskmitigationtemplateIdcheck',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskMitigationTemplateResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/risk/mitigation/template/:id/status/:status',
    alias: 'putV1riskmitigationtemplateIdstatusStatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'status',
        type: 'Path',
        schema: z.enum(['Active', 'Inactive']),
      },
    ],
    response: RiskMitigationTemplateResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/risk/mitigation/template/organisation/:orgId',
    alias: 'getV1riskmitigationtemplateorganisationOrgId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'orgId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'ignoreStatus',
        type: 'Query',
        schema: z.boolean().optional().default(false),
      },
      {
        name: 'lastIndex',
        type: 'Query',
        schema: z.number().int().optional().default(24),
      },
      {
        name: 'startIndex',
        type: 'Query',
        schema: z.number().int().optional().default(0),
      },
    ],
    response: RiskMitigationTemplatePageResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/soletrader',
    alias: 'postV1soletrader',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SoleTrader,
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/soletrader/:id',
    alias: 'putV1soletraderId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SoleTrader,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/support_document',
    alias: 'getV1support_document',
    requestFormat: 'json',
    parameters: [
      {
        name: 'documentType',
        type: 'Query',
        schema: z
          .enum([
            'CertificateOfTitle',
            'NationalVendorDeclarationForLivestock',
            'Other',
            'RatesNotice',
            'ValuationNotice',
            'WholesaleCertificate',
          ])
          .optional(),
      },
      {
        name: 'entityName',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'lastIndex',
        type: 'Query',
        schema: z.number().int().optional().default(24),
      },
      {
        name: 'orderBy',
        type: 'Query',
        schema: orderBy__2,
      },
      {
        name: 'orderDirection',
        type: 'Query',
        schema: orderDirection,
      },
      {
        name: 'startIndex',
        type: 'Query',
        schema: z.number().int().optional().default(0),
      },
      {
        name: 'status',
        type: 'Query',
        schema: z.enum(['Draft', 'InProgress', 'Rejected', 'Verified']).optional(),
      },
    ],
    response: SupportDocumentPaginationResponse,
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the admin role to access this endpoint.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/support_document/:supportDocumentId',
    alias: 'putV1support_documentSupportDocumentId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateSupportDocumentRequest,
      },
      {
        name: 'supportDocumentId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: SupportDocument,
    errors: [
      {
        status: 400,
        description: `Invalid request`,
        schema: z.array(ValidationResult),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Support document not found`,
        schema: z.array(ValidationResult),
      },
      {
        status: 409,
        description: `Support document update conflict occurred`,
        schema: SupportDocument,
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/support_document/:supportDocumentId',
    alias: 'getV1support_documentSupportDocumentId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'supportDocumentId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: SupportDocument,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Support document not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v1/support_document/:supportDocumentId',
    alias: 'deleteV1support_documentSupportDocumentId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'supportDocumentId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Support document not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/support_document/:supportDocumentId/byAdmin',
    alias: 'putV1support_documentSupportDocumentIdbyAdmin',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SupportDocument,
      },
      {
        name: 'supportDocumentId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: SupportDocument,
    errors: [
      {
        status: 400,
        description: `Invalid request`,
        schema: z.array(ValidationResult),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Support document not found`,
        schema: z.array(ValidationResult),
      },
      {
        status: 409,
        description: `Support document update conflict occurred`,
        schema: SupportDocument,
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/support_document/:supportDocumentId/status',
    alias: 'putV1support_documentSupportDocumentIdstatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateSupportDocumentStatusRequest,
      },
      {
        name: 'supportDocumentId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: SupportDocument,
    errors: [
      {
        status: 400,
        description: `Invalid request`,
        schema: ValidationFailedResponse,
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Support document not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/support_document/customer/:entityId',
    alias: 'getV1support_documentcustomerEntityId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'entityId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.array(SupportDocument),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Customer entity not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/support_document/customer/:entityId',
    alias: 'postV1support_documentcustomerEntityId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateSupportDocumentRequest,
      },
      {
        name: 'entityId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: SupportDocument,
    errors: [
      {
        status: 400,
        description: `Invalid request`,
        schema: ValidationFailedResponse,
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Customer entity or file not found`,
        schema: z.array(ValidationResult),
      },
      {
        status: 409,
        description: `Support document already exists`,
        schema: z.array(ValidationResult),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/support_document/customer/:entityId/sub_types',
    alias: 'getV1support_documentcustomerEntityIdsub_types',
    requestFormat: 'json',
    parameters: [
      {
        name: 'entityId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.array(SupportDocumentSubTypeWithAllowedStatus),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Customer entity not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/task/:id',
    alias: 'getV1taskId',
    description: `This method shows task information`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: TaskLookupResponse,
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Only users with admin or client role is allowed.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Customer specified by customerId could not be found.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/task/:type/customer/:customerId',
    alias: 'postV1taskTypecustomerCustomerId',
    description: `This method creates a task of a customer. Note: this API is deprecated`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({}).partial().passthrough(),
      },
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'type',
        type: 'Path',
        schema: z.enum([
          'FACE_CHECK',
          'WHOLESALE_CERTIFICATE',
          'REVIEW_REJECTED_IDENTITY',
          'CREDENTIAL_SETUP',
        ]),
      },
    ],
    response: TaskCreatedResponse,
    errors: [
      {
        status: 400,
        description: `Invalid task type.`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Only users with admin or client role is allowed.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Customer specified by customerId could not be found.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/task/customer/:customerId',
    alias: 'getV1taskcustomerCustomerId',
    description: `This method list all tasks of a customer`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'type',
        type: 'Query',
        schema: z
          .enum([
            'FACE_CHECK',
            'WHOLESALE_CERTIFICATE',
            'REVIEW_REJECTED_IDENTITY',
            'CREDENTIAL_SETUP',
          ])
          .optional(),
      },
    ],
    response: z.array(TaskLookupResponse),
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Only users with admin or client role is allowed.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Customer specified by customerId could not be found.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/task/customer/:customerId',
    alias: 'postV1taskcustomerCustomerId',
    description: `This method creates a task of a customer.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TaskCreationRequest,
      },
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: TaskCreatedResponse,
    errors: [
      {
        status: 400,
        description: `Invalid task type.`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Only users with admin or client role is allowed.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Customer specified by customerId could not be found.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/tracking-reference/report/clients',
    alias: 'getV1trackingReferencereportclients',
    description: `Returns organisations that have no parent, in any status, ordered by name then id. Each item carries the number of organisations in its tree: the organisation itself and every organisation beneath it, at any depth and in any status.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'lastIndex',
        type: 'Query',
        schema: z.number().int().optional().default(24),
      },
      {
        name: 'search',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'startIndex',
        type: 'Query',
        schema: z.number().int().optional().default(0),
      },
    ],
    response: TrackingReferenceReportClientPageResponse,
    errors: [
      {
        status: 401,
        description: `Authentication required - user not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Access denied - admin role required`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/tracking-reference/report/organisation/:organisationId',
    alias: 'getV1trackingReferencereportorganisationOrganisationId',
    description: `Returns one row per tracking reference issued under the organisation or any organisation beneath it, in any status. Task invites and references without an organisation code are not included. Rows are ordered by status (Inactive, Active, Completed), then references without an identity before those with one, then longest outstanding first. Rows that tie on those three keep the order of the query: organisation name, then status, then newest invitation first, then tracking reference id. The page is a slice of that order. Days since invited counts to the current date in Australia/Sydney. With format internalXlsx or clientXlsx the response is an xlsx file of every row of the tree in the same order, and startIndex and lastIndex are ignored. The client file leaves out Entities (with status) and leaves Identity Created blank on Inactive rows. The file name is in the Content-Disposition header.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'organisationId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'format',
        type: 'Query',
        schema: format__2,
      },
      {
        name: 'lastIndex',
        type: 'Query',
        schema: z.number().int().optional().default(24),
      },
      {
        name: 'startIndex',
        type: 'Query',
        schema: z.number().int().optional().default(0),
      },
    ],
    response: TrackingReferenceReportPageResponse,
    errors: [
      {
        status: 400,
        description: `The organisation has a parent organisation`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Authentication required - user not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Access denied - admin role required`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The organisation does not exist`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/v1/tracking/:trackingReference/activate',
    alias: 'patchV1trackingTrackingReferenceactivate',
    description: `Activate a tracking reference, if the tracking reference is not already activated, and decide the onboard-as mode of a sign-up for the customer and client. A sign-up decided AskFirst can be combined with other invites of the same customer and client (see the joinable-invites operation). At most one entity type joins. That operation says which invites join. An entity invite that does not join is completed through its own link.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `Optional. The onboard-as mode the portal asks for; absent means Legacy.`,
        type: 'Body',
        schema: z.object({ requestedOnboardAsMode: z.string() }).partial().passthrough().optional(),
      },
      {
        name: 'trackingReference',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: TrackingReferenceActivationResponse,
    errors: [
      {
        status: 400,
        description: `Validation error`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not allowed to activate tracking reference for another user`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Tracking reference not found`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `The tracking reference is already completed. The body carries the errorCode INVITE_COMPLETED.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/tracking/:trackingReference/confirmation',
    alias: 'putV1trackingTrackingReferenceconfirmation',
    description: `Confirm who the customer is onboarding for the client of an invite decided AskFirst: an entity type, or Individual for the customer alone. Sets the customer&#x27;s requests for the client. The choice is judged against the invites that join the invite (see the joinable-invites operation). Those invites declare at most one entity type.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EntityConfirmation,
      },
      {
        name: 'trackingReference',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Malformed tracking reference or body, a choice the client&#x27;s onboarding workflow does not take, or a choice outside what the invites that join the invite allow`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `The tracking reference is another user&#x27;s`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Tracking reference not found`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `Not confirmable: the invite was not decided AskFirst or is completed, a request in today&#x27;s flow is open for the client, the choice is locked, the confirmed entity type is one the client already holds from outside this journey and the invite the new request would record declares the same readable organisation name as the invite that closed a request for it, or Individual is confirmed while the client already holds the Individual from outside this journey. The choice is locked when a grant has already closed the request that holds the partner reference data and the customer asks for a different choice, when &quot;just me&quot; is asked while the entity request is open and the Individual request is granted, and the partner reference data would be left on no request, or when the change would replace the open request of this invite with a request that another invite opened. The two duplicate identity refusals (the held entity type with the same organisation name, and Individual while the client holds the Individual) carry the errorCode DUPLICATE_IDENTITY_REQUEST in the body. A completed invite carries the errorCode INVITE_COMPLETED, an invite not decided AskFirst carries INVITE_NOT_DECIDED_ASK_FIRST, a request in today&#x27;s flow that is open for the client carries OPEN_LEGACY_PERMISSION_REQUEST, and a locked choice, the replacement of the open request of this invite included, carries ENTITY_CHOICE_LOCKED. A customer who holds a document of a type that requires opt-in, where the invite&#x27;s organisation code or another code the customer onboards through has not opted in to that type, carries HELD_DOCUMENT_TYPE_NOT_ACCEPTED.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/tracking/:trackingReference/joinable-invites',
    alias: 'getV1trackingTrackingReferencejoinableInvites',
    description: `For an invite decided AskFirst, list the invites that the confirmation judges the customer&#x27;s choice against. The opened invite comes first. The others are invites of the same customer and client that the customer has activated, that were created no later than the first activation of the opened invite, that declare an entity type or Individual, and that do not conflict with another invite over which partner record applies. The confirmation makes the same call on the same set, so it judges the choice against these invites and the declared entity type that governs them, which the response also gives. An entity invite is an invite that declares an entity type other than Individual. At most one entity type joins. The entity invites that join have declarations that overlap each other. For example, a group and an exact type in that group can join together. When the opened invite declares an entity type, only entity invites with an overlapping declaration join. An entity invite for another entity type does not join. When the opened invite declares Individual or no entity type, the open entity invites join only if their declarations overlap each other. If two open entity invites are for different entity types, no entity invite joins. An entity invite that does not join is completed through its own link. Invites that declare Individual are not entity invites. They join by the partner record rule alone. The response returns no partner record, callback URL or customer metadata. Only the customer who owns the invite may read it.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'trackingReference',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: JoinableInvitesResponse,
    errors: [
      {
        status: 400,
        description: `Malformed tracking reference`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User is not authenticated, or must step up`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `The tracking reference is another user&#x27;s`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Tracking reference not found`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `The latest activation of the invite did not decide AskFirst, or the invite was never activated. There is nothing to combine: the client continues with this invite alone. The body carries the errorCode INVITE_NOT_DECIDED_ASK_FIRST.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/tracking/:trackingReference/ownership',
    alias: 'getV1trackingTrackingReferenceownership',
    description: `Read-only: report whether the authenticated principal owns the tracking reference. Enforces nothing; activation remains the authoritative ownership gate.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'trackingReference',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.object({ owned: z.boolean() }).passthrough(),
    errors: [
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/trust',
    alias: 'postV1trust',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateTrustRequest,
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v1/trust/:id',
    alias: 'putV1trustId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateTrustRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/trust/:id',
    alias: 'getV1trustId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: Trust,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/user/customer',
    alias: 'postV1usercustomer',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateUserRequest,
      },
    ],
    response: UserResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/user/invite',
    alias: 'postV1userinvite',
    description: `Invite a user to the system, note this API is intended for use by client organisations who wishes to integrate with VerifiMe.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UserInviteRequest,
      },
    ],
    response: UserInviteResponse,
    errors: [
      {
        status: 400,
        description: `Validation error. Possible causes: invalid email format, invalid or inactive organisation code, invalid characters in customerContext name fields (digits and special characters are not accepted), a declaredEntityType that the onboarding workflow type of the organisation code does not allow, or a declaredOrganisationName that is longer than 255 characters or contains a control character (Unicode category Cc) that is not removed as leading or trailing whitespace, or an unpaired surrogate; a no-break space in that name is not a cause, because it is replaced by an ordinary space. When validation rules detect the error, the response body contains a validationResults array identifying the failing field and error code. When the error is a deserialisation failure (e.g. malformed date, or a declaredEntityType that is not one of the listed values; the values are case sensitive), the response body contains a simple error message without validationResults.`,
        schema: ValidationFailedResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/user/me',
    alias: 'getV1userme',
    requestFormat: 'json',
    response: UserResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/v1/user/me/credentialSetupStatus',
    alias: 'patchV1usermecredentialSetupStatus',
    description: `Deprecated: with_credentials is now set server-side by the credential-sync pipeline via the internal endpoint PATCH /internal/v1/user/{username}/credential-status. This browser endpoint is retained for backward compatibility and is no longer called.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ withCredentials: z.boolean() }).partial().passthrough(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 406,
        description: `The user is not allowed to reset the credential setup status to false`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/user/me/primaryIndividual',
    alias: 'getV1usermeprimaryIndividual',
    description: `Lookup the primary individual identity of the current authenticated user.`,
    requestFormat: 'json',
    response: Person.nullable(),
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the customer role to access this endpoint.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/user/primaryIndividual',
    alias: 'getV1userprimaryIndividual',
    description: `Lookup the primary individual identity of a user by email or username.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'email',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'username',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: Person,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The user does not exist.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/user/selfsignup/tracking',
    alias: 'postV1userselfsignuptracking',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SelfSignUpRequest,
      },
    ],
    response: SelfSignUpResponse,
    errors: [
      {
        status: 400,
        description: `Validation error`,
        schema: z.array(ValidationResult),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/user/signup/tracking/:trackingReference/action',
    alias: 'postV1usersignuptrackingTrackingReferenceaction',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ email: z.string() }).partial().passthrough(),
      },
      {
        name: 'trackingReference',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: InvitationTrackingActionResponse,
    errors: [
      {
        status: 400,
        description: `Validation error, or the email is unknown or is not the tracking reference&#x27;s`,
        schema: z.array(ValidationResult),
      },
      {
        status: 404,
        description: `Tracking reference not found`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `Tracking reference is completed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/user/signup/tracking/:trackingReference/primaryIndividual',
    alias: 'getV1usersignuptrackingTrackingReferenceprimaryIndividual',
    description: `Lookup the primary individual identity referenced by the tracking reference.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'trackingReference',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: Person,
    errors: [
      {
        status: 401,
        description: `Not Authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Requires Administrator role`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/user/signup/tracking/:trackingReference/status',
    alias: 'getV1usersignuptrackingTrackingReferencestatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'trackingReference',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: TrackingReferenceStatusResponse,
    errors: [
      {
        status: 304,
        description: `The status has not changed since the supplied ETag.`,
        schema: z.void(),
      },
      {
        status: 400,
        description: `Invalid tracking reference supplied.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v1/user/signupInvite',
    alias: 'postV1usersignupInvite',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SignupEmailRequest,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/user/username',
    alias: 'getV1userusername',
    requestFormat: 'json',
    parameters: [
      {
        name: 'email',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `User does not exist.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/workflow/metadata/customer/:customerId',
    alias: 'getV1workflowmetadatacustomerCustomerId',
    description: `Returns a paginated list of workflow metadata with organisation code.
Organisation IDs are derived from all active permissions for the customer;
then organisation codes are resolved for those organisations. All permissions and all metadata
for each permissions-org -&gt; orgCodes are returned (sorted and paginated).
`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'lastIndex',
        type: 'Query',
        schema: z.number().int().optional().default(24),
      },
      {
        name: 'orderBy',
        type: 'Query',
        schema: z
          .enum(['organisationCodeUpdatedDateTime', 'organisationName'])
          .optional()
          .default('organisationName'),
      },
      {
        name: 'orderDirection',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional().default('asc'),
      },
      {
        name: 'startIndex',
        type: 'Query',
        schema: z.number().int().optional().default(0),
      },
    ],
    response: WorkflowMetadataPageResponse,
    errors: [
      {
        status: 400,
        description: `Invalid customerId format or invalid query parameters.`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the customer or admin role to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Customer not found.`,
        schema: z.void(),
      },
      {
        status: 410,
        description: `Customer is redacted.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/workflow/metadata/organisationCode/:organisationCode',
    alias: 'getV1workflowmetadataorganisationCodeOrganisationCode',
    description: `Public, pre-sign-in lookup for the sign-up landing page. Returns the organisation name, code,
logo URL, workflow type and identity document count of an active organisation code. Creates no
tracking reference. The logo URL is null when the organisation has no logo or the logo lookup fails.
`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'organisationCode',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: WorkflowMetadataByOrganisationCodeResponse,
    errors: [
      {
        status: 404,
        description: `Organisation code not found, inactive, without an organisation, or not in the valid code format. All cases share one generic body. There is no 400: a malformed code is a 404.`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Unexpected server error, for example the database is unavailable, or the organisation&#x27;s identity document requirement is misconfigured (not 1 or 2).`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/workflow/metadata/tracking/:trackingReference',
    alias: 'getV1workflowmetadatatrackingTrackingReference',
    description: `Returns a paginated list of workflow metadata with organisation code. Supports both
organisationCodeId-based (signup) and taskId-based tracking references.
`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'trackingReference',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'lastIndex',
        type: 'Query',
        schema: z.number().int().optional().default(24),
      },
      {
        name: 'orderBy',
        type: 'Query',
        schema: z
          .literal('organisationCodeUpdatedDateTime')
          .optional()
          .default('organisationCodeUpdatedDateTime'),
      },
      {
        name: 'orderDirection',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional().default('asc'),
      },
      {
        name: 'startIndex',
        type: 'Query',
        schema: z.number().int().optional().default(0),
      },
    ],
    response: WorkflowMetadataPageResponse,
    errors: [
      {
        status: 400,
        description: `Invalid tracking reference format or invalid query parameters.`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User must be authenticated to access task-based tracking references.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the customer or admin role to access task-based tracking references.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Tracking reference not found or cannot be resolved.`,
        schema: z.void(),
      },
      {
        status: 410,
        description: `Customer associated with the task is redacted.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/customer',
    alias: 'getV2customer',
    requestFormat: 'json',
    parameters: [
      {
        name: 'beginIndex',
        type: 'Query',
        schema: z.number().int().optional().default(0),
      },
      {
        name: 'criteria',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'endIndex',
        type: 'Query',
        schema: z.number().int().optional().default(24),
      },
      {
        name: 'name',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'orderBy',
        type: 'Query',
        schema: orderBy__3,
      },
      {
        name: 'orderDirection',
        type: 'Query',
        schema: orderDirection,
      },
      {
        name: 'status',
        type: 'Query',
        schema: z.enum(['Draft', 'InProgress', 'Rejected', 'Verified']).optional(),
      },
      {
        name: 'type',
        type: 'Query',
        schema: z
          .enum([
            'Individual_Domestic',
            'Individual_Foreign',
            'SMSF',
            'Sole_Trader',
            'Partnership',
            'Private_Company',
            'Private_Foreign_Company',
            'Public_Company',
            'Public_Foreign_Company',
            'Trust_Regulated',
            'Trust_Unregulated',
            'Charity_Association',
          ])
          .optional(),
      },
    ],
    response: CustomerPaginationResponse,
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the admin role to access this endpoint.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/organisation',
    alias: 'postV2organisation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: OrganisationCreateRequest,
      },
    ],
    response: OrganisationResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Bearer token is not specified or incorrect`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Admin role required`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/organisation/clone/from/:fromSubEntityId/to/:toSubEntityId',
    alias: 'postV2organisationclonefromFromSubEntityIdtoToSubEntityId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'fromSubEntityId',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'toSubEntityId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Entity IDs must start with &#x27;SE-&#x27;, which are sub-entity entity IDs`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Organisation with entity id specified by fromSubEntityId or toSubEntityId not found`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `Sub-entity of toSubEntityId already has rules and mitigation templates`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/pepsanction/customer/:customerId',
    alias: 'getV2pepsanctioncustomerCustomerId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.array(PEPSanction),
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the client or admin role to access this endpoint, further more if the user is a client user, the target customer must have permissioned the client organisation of the user.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The customer specified by customerId was not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/pepsanction/customer/:customerId',
    alias: 'postV2pepsanctioncustomerCustomerId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreatePEPSanctionRequest,
      },
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: PEPSanction,
    errors: [
      {
        status: 400,
        description: `The request was invalid.`,
        schema: z.array(ValidationResult),
      },
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the admin role to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The customer specified by customerId was not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/pepsanction/customer/:customerId/latest',
    alias: 'getV2pepsanctioncustomerCustomerIdlatest',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: PEPSanction.nullable(),
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the client or admin role to access this endpoint, further more if the user is a client user, the target customer must have permissioned the client organisation of the user.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The customer specified by customerId was not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/permission/organisation/:orgId',
    alias: 'getV2permissionorganisationOrgId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'orgId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'currentRiskLevel',
        type: 'Query',
        schema: z.enum(['Pending', 'Low', 'Medium', 'High', 'Extreme']).optional(),
      },
      {
        name: 'customerName',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'entityType',
        type: 'Query',
        schema: z
          .enum([
            'Individual_Domestic',
            'Individual_Foreign',
            'SMSF',
            'Sole_Trader',
            'Partnership',
            'Private_Company',
            'Private_Foreign_Company',
            'Public_Company',
            'Public_Foreign_Company',
            'Trust_Regulated',
            'Trust_Unregulated',
            'Charity_Association',
          ])
          .optional(),
      },
      {
        name: 'format',
        type: 'Query',
        schema: format__3,
      },
      {
        name: 'fromLastAssessed',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'fromPermissionAdded',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'isOverridden',
        type: 'Query',
        schema: z.boolean().optional(),
      },
      {
        name: 'lastIndex',
        type: 'Query',
        schema: z.number().int().optional().default(24),
      },
      {
        name: 'orderBy',
        type: 'Query',
        schema: z
          .enum([
            'customerName',
            'entityType',
            'permissionStatus',
            'lastAssessedAt',
            'currentRiskTitle',
            'currentRiskScore',
            'presentedRiskTitle',
            'presentedRiskScore',
            'wholesaleCertificateStatus',
            'referenceData',
          ])
          .optional(),
      },
      {
        name: 'orderDirection',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional(),
      },
      {
        name: 'overrideEffect',
        type: 'Query',
        schema: z.enum(['None', 'Escalated', 'Mitigated']).optional(),
      },
      {
        name: 'permissionStatuses',
        type: 'Query',
        schema: z.array(PermissionStatus).optional(),
      },
      {
        name: 'presentedRiskLevel',
        type: 'Query',
        schema: z.enum(['Pending', 'Low', 'Medium', 'High', 'Extreme']).optional(),
      },
      {
        name: 'referenceData',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'startIndex',
        type: 'Query',
        schema: z.number().int().optional().default(0),
      },
      {
        name: 'toLastAssessed',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'toPermissionAdded',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'wholesaleCertificateStatus',
        type: 'Query',
        schema: z.enum(['Draft', 'InProgress', 'Rejected', 'Verified']).optional(),
      },
      {
        name: 'xlsxTimeZoneId',
        type: 'Query',
        schema: z.string().optional().default('Australia/Sydney'),
      },
    ],
    response: PermissionCustomerSummaryV2PageResponse,
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have permission to read permissions for this organisation.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/permission/organisation/:orgId/customer/:customerId',
    alias: 'getV2permissionorganisationOrgIdcustomerCustomerId',
    description: `Read-only. Callable by human client/admin users and by internal service-to-service callers (e.g. the risk-assessment-notification-producer-lambda) via the shared M2M client.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'orgId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: PermissionCustomerSummaryV2,
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Caller cannot read this customer (authorisation is scoped to the customer; a customer the caller has no read access to, including one that does not exist, is denied here rather than disclosed).`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The customer is readable but has no permission summary within this organisation.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/person',
    alias: 'postV2person',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreatePersonRequest,
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/person/:customerId/liveness/transaction/latest',
    alias: 'getV2personCustomerIdlivenesstransactionlatest',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: LivenessTransactionWithPresignedPortraitImageUrlResponse.nullable(),
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the admin or client role, and client must have the rights to read customer (i.e. permission granted to client.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `No customer was found with the specified ID.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/person/:customerId/liveness/transaction/latest/video',
    alias: 'getV2personCustomerIdlivenesstransactionlatestvideo',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: LivenessTransactionWithPresignedVideoUrlResponse.nullable(),
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the admin or client role, and client must have the rights to read customer (i.e. permission granted to client.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `No customer found with specified ID`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/person/:customerId/portrait/comparison',
    alias: 'getV2personCustomerIdportraitcomparison',
    requestFormat: 'json',
    parameters: [
      {
        name: 'customerId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: PortraitComparisonResponse.nullable(),
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User must have the admin or client role, and client must have the rights to read customer (i.e. permission granted to client.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v2/person/:id',
    alias: 'putV2personId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdatePersonRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: CustomerResponse,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Not Allowed`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v2/person/:id/identityDocument/:type',
    alias: 'putV2personIdidentityDocumentType',
    description: `Updates the specified identity document type for a person with the provided information`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `Request to which document to be updated.`,
        type: 'Body',
        schema: UpdateIdentityDocumentRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'type',
        type: 'Path',
        schema: z.enum([
          'Passport',
          'DriverLicence',
          'OtherIdentityDocument',
          'MedicareCard',
          'PhotoCard',
          'ImmiCard',
        ]),
      },
    ],
    response: IdentityDocumentResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request data, or a document type that requires opt-in and that an organisation code the person onboards through has not opted in to (or the person onboards through no code). The opt-in refusal carries the errorCode DOCUMENT_TYPE_NOT_OPTED_IN in the body.`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to update the identity document`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Person not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/person/:id/identityDocument/:type',
    alias: 'getV2personIdidentityDocumentType',
    description: `Retrieves the specified identity document type for a person`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'type',
        type: 'Path',
        schema: z.enum([
          'Passport',
          'DriverLicence',
          'OtherIdentityDocument',
          'MedicareCard',
          'PhotoCard',
          'ImmiCard',
        ]),
      },
    ],
    response: IdentityDocumentResponse.nullable(),
    errors: [
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to view the identity document`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/v2/person/:id/identityDocument/:type',
    alias: 'patchV2personIdidentityDocumentType',
    description: `User letting system know that they have completed the update of their identity document. `,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'type',
        type: 'Path',
        schema: z.enum([
          'Passport',
          'DriverLicence',
          'OtherIdentityDocument',
          'MedicareCard',
          'PhotoCard',
          'ImmiCard',
        ]),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to update the identity document`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Person or identity document not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v2/person/:id/identityDocument/:type/captureStatus',
    alias: 'putV2personIdidentityDocumentTypecaptureStatus',
    description: `Updates the capture status of the specified identity document type for a person`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateIdentityDocumentCaptureStatusRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'type',
        type: 'Path',
        schema: z.enum([
          'Passport',
          'DriverLicence',
          'OtherIdentityDocument',
          'MedicareCard',
          'PhotoCard',
          'ImmiCard',
        ]),
      },
    ],
    response: z.enum(['Incomplete', 'Passed', 'Failed']),
    errors: [
      {
        status: 400,
        description: `Invalid status value`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to update the capture status`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Identity document not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/person/:id/identityDocument/:type/captureStatus',
    alias: 'getV2personIdidentityDocumentTypecaptureStatus',
    description: `Retrieves the current capture status of the specified identity document type for a person. Returns Incomplete if the identity document is not found.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'type',
        type: 'Path',
        schema: z.enum([
          'Passport',
          'DriverLicence',
          'OtherIdentityDocument',
          'MedicareCard',
          'PhotoCard',
          'ImmiCard',
        ]),
      },
    ],
    response: z.enum(['Incomplete', 'Passed', 'Failed']),
    errors: [
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to view the capture status`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Person not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/person/:id/identityDocument/:type/file',
    alias: 'getV2personIdidentityDocumentTypefile',
    description: `Retrieves all existing files of a specific identity document type for a person. This endpoint returns details about each file including its ID, filename, size, and last modified date. A scan result ID is also provided which can be used to retrieve scan results for all files associated with this person and identity document type. This operation is restricted to users with appropriate permissions for the specified person.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'type',
        type: 'Path',
        schema: z.enum([
          'Passport',
          'DriverLicence',
          'OtherIdentityDocument',
          'MedicareCard',
          'PhotoCard',
          'ImmiCard',
        ]),
      },
    ],
    response: IdentityDocumentFileItemResponse,
    errors: [
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to retrieve the types of file for the specified person`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v2/person/:id/identityDocument/:type/file/:fileId',
    alias: 'deleteV2personIdidentityDocumentTypefileFileId',
    description: `Retrieves all remaining  files of a specific identity document type for a person. This endpoint returns details about each file including its ID, filename, size, and last modified date. A scan result ID is also provided which can be used to retrieve scan results for all files associated with this person and identity document type. This operation is restricted to users with appropriate permissions for the specified person.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'fileId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'type',
        type: 'Path',
        schema: z.enum([
          'Passport',
          'DriverLicence',
          'OtherIdentityDocument',
          'MedicareCard',
          'PhotoCard',
          'ImmiCard',
        ]),
      },
    ],
    response: IdentityDocumentFileItemResponse,
    errors: [
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to delete specific file of the types of file for the specified person`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/person/:id/identityDocument/:type/presign',
    alias: 'postV2personIdidentityDocumentTypepresign',
    description: `Upload presign url for identity document for a person. If there are multiple files, a presign url will be returned for each file. Each presign url is used to upload a file to S3. The scan result id is used to get the scan result after the file is uploaded. Each presign url is temporary and will expire after some time. Allowed file types: pdf, jpg, png, jpeg. The files to be uploaded will be scanned by the system. The result of the scan can be retrieved later using the ID provided in the response. The scan result will be relevant for all files uploaded for the specified person and their identity document type. `,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `List of file names to be uploaded. Each file name should be unique.`,
        type: 'Body',
        schema: z.array(z.string()),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'type',
        type: 'Path',
        schema: z.enum([
          'Passport',
          'DriverLicence',
          'OtherIdentityDocument',
          'MedicareCard',
          'PhotoCard',
          'ImmiCard',
        ]),
      },
    ],
    response: IdentityDocumentUploadPresignResponse,
    errors: [
      {
        status: 400,
        description: `A file name does not end in an allowed file type (pdf, jpg, png or jpeg), or the document type requires opt-in and an organisation code the person onboards through has not opted in to it (or the person onboards through no code). The opt-in refusal carries the errorCode DOCUMENT_TYPE_NOT_OPTED_IN in the body, and no URL is issued. A person who already holds the document keeps it, but only an admin may add a file to it.`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to upload the types of file for the specified person`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/person/:id/identityDocument/:type/scanResult/:identityDocumentScanId',
    alias: 'getV2personIdidentityDocumentTypescanResultIdentityDocumentScanId',
    description: `Retrieves a specific scan result by ID for the specified identity document type`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'identityDocumentScanId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'type',
        type: 'Path',
        schema: z.enum([
          'Passport',
          'DriverLicence',
          'OtherIdentityDocument',
          'MedicareCard',
          'PhotoCard',
          'ImmiCard',
        ]),
      },
    ],
    response: ScanIdentityDocumentResultResponse.nullable(),
    errors: [
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to view the scan result`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/person/:id/identityDocument/:type/scanResult/latest',
    alias: 'getV2personIdidentityDocumentTypescanResultlatest',
    description: `Retrieves the most recent scan result for the specified identity document type`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'type',
        type: 'Path',
        schema: z.enum([
          'Passport',
          'DriverLicence',
          'OtherIdentityDocument',
          'MedicareCard',
          'PhotoCard',
          'ImmiCard',
        ]),
      },
    ],
    response: ScanIdentityDocumentResultResponse.nullable(),
    errors: [
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to view the scan result`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v2/person/:id/identityDocument/selection',
    alias: 'putV2personIdidentityDocumentselection',
    description: `Update the selection of identity document types associated with a specific person. This operation replaces any existing selection with the provided list. Any previously uploaded files for no longer selected identity documents will be deleted. Any previously captured data for no longer selected identity documents will be deleted as well.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.array(IdentityDocumentType),
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `The request body is not a list of identity document types, or a selected document type requires opt-in, the person does not already hold it, and an organisation code the person onboards through has not opted in to it (or the person onboards through no code). The opt-in refusal carries the errorCode DOCUMENT_TYPE_NOT_OPTED_IN in the body, and nothing is changed.`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to update the selection for the specified person`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/person/:id/identityDocument/selection',
    alias: 'getV2personIdidentityDocumentselection',
    description: `Retrieve the currently selected identity document types associated with a specific person. If no selection has been made, an empty list is returned.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.array(IdentityDocumentType),
    errors: [
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to view the selection for the specified person`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/person/:id/portrait',
    alias: 'postV2personIdportrait',
    description: `Manual update portrait urls for an individual. Note: this endpoint is deprecated. use POST /v1/person/{id}/portrait instead`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdatePortraitRequest,
      },
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: IdentityPortraitItemResponse,
    errors: [
      {
        status: 400,
        description: `FileId is not supplied`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to update portrait`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/person/:id/portrait',
    alias: 'getV2personIdportrait',
    description: `Get the latest portrait url of an individual, no matter what type is. Note: this endpoint is deprecated. use GET /v1/person/{id}/portrait instead`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: IdentityPortraitItemResponse,
    errors: [
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to view detail of the individual`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Portrait is not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/person/me/identityDocument/requirement',
    alias: 'getV2personmeidentityDocumentrequirement',
    description: `Get identity document requirement for an individual onboarding to VerifiMe client organisation. The requirement is determined by the client organisation, and should only be called for the purpose of initial onboarding process (i.e. Individual Wizard workflow).  For additional risk assessment mitigation,  or failed verification, the requirement should be derived from upload document task.`,
    requestFormat: 'json',
    response: IdentityDocumentRequirementV2Response,
    errors: [
      {
        status: 401,
        description: `User is not authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User is not authorized to be onboarded`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/risk/assessment/:id',
    alias: 'getV2riskassessmentId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskAssessmentV2,
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have permission to read this assessment`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Risk assessment not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/risk/assessment/:raId/mitigation/:mitId/comment',
    alias: 'postV2riskassessmentRaIdmitigationMitIdcomment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `The comment to append`,
        type: 'Body',
        schema: z.object({ comment: z.string() }).partial().passthrough(),
      },
      {
        name: 'mitId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'raId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskMitigationV2,
    errors: [
      {
        status: 400,
        description: `Invalid request body (blank comment)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User must be authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have permission to update this assessment`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Assessment or mitigation not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'put',
    path: '/v2/risk/assessment/:raId/mitigation/:mitId/status',
    alias: 'putV2riskassessmentRaIdmitigationMitIdstatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `The status outcome and optional comment`,
        type: 'Body',
        schema: MitigationStatusRequest,
      },
      {
        name: 'mitId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'raId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskAssessmentV2,
    errors: [
      {
        status: 400,
        description: `Invalid request body`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User must be authenticated`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have permission to update this assessment`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Assessment or mitigation not found`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `Mitigations are locked while an override is active`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/risk/assessment/:riskAssessmentId/override',
    alias: 'getV2riskassessmentRiskAssessmentIdoverride',
    requestFormat: 'json',
    parameters: [
      {
        name: 'riskAssessmentId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskAssessmentOverrideResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have permission to read this assessment`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `No active override exists, or risk assessment not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'delete',
    path: '/v2/risk/assessment/:riskAssessmentId/override',
    alias: 'deleteV2riskassessmentRiskAssessmentIdoverride',
    requestFormat: 'json',
    parameters: [
      {
        name: 'riskAssessmentId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskAssessmentOverrideResponse,
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have permission to update this assessment`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `No active override exists, or risk assessment not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/risk/assessment/:riskAssessmentId/override',
    alias: 'postV2riskassessmentRiskAssessmentIdoverride',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        description: `The override risk level and reason`,
        type: 'Body',
        schema: CreateRiskAssessmentOverrideRequest,
      },
      {
        name: 'riskAssessmentId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskAssessmentOverrideResponse,
    errors: [
      {
        status: 400,
        description: `Validation error: missing or invalid fields`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have permission to update this assessment`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Risk assessment not found`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `State conflict: assessment not open, an active override already exists, or the override level equals the current risk`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/risk/assessment/:riskAssessmentId/override/history',
    alias: 'getV2riskassessmentRiskAssessmentIdoverridehistory',
    requestFormat: 'json',
    parameters: [
      {
        name: 'riskAssessmentId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: z.array(RiskAssessmentOverrideResponse),
    errors: [
      {
        status: 401,
        description: `Not Authorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have permission to read this assessment`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Risk assessment not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/v2/risk/assessment/organisation/:orgId',
    alias: 'postV2riskassessmentorganisationOrgId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RunAssessmentRequest,
      },
      {
        name: 'orgId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: RiskAssessmentV2,
    errors: [
      {
        status: 400,
        description: `Validation failure (missing customerId)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have permission to run a risk assessment for this customer and organisation`,
        schema: z.void(),
      },
      {
        status: 409,
        description: `Risk assessment cannot be run. Use the eligibility check endpoint for details.`,
        schema: z.void(),
      },
      {
        status: 504,
        description: `The risk assessment run did not complete within the timeout; it may still finish server-side`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/risk/assessment/organisation/:orgId',
    alias: 'getV2riskassessmentorganisationOrgId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'orgId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'assessedRiskLevel',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'assessmentStatus',
        type: 'Query',
        schema: z.enum(['NoRisksDetected', 'Open', 'Closed']).optional(),
      },
      {
        name: 'currentRiskLevel',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'customerId',
        type: 'Query',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid()
          .optional(),
      },
      {
        name: 'fromDateCreated',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'fromLastUpdated',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'isOverridden',
        type: 'Query',
        schema: z.boolean().optional(),
      },
      {
        name: 'lastIndex',
        type: 'Query',
        schema: z.number().int().optional().default(24),
      },
      {
        name: 'orderBy',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'orderDirection',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'presentedRiskLevel',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'startIndex',
        type: 'Query',
        schema: z.number().int().optional().default(0),
      },
      {
        name: 'toDateCreated',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'toLastUpdated',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: RiskAssessmentV2PageResponse,
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have permission to read assessments for this organisation.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/risk/assessment/organisation/:orgId/eligibility',
    alias: 'getV2riskassessmentorganisationOrgIdeligibility',
    requestFormat: 'json',
    parameters: [
      {
        name: 'orgId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'customerId',
        type: 'Query',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
    ],
    response: EligibilityResponse,
    errors: [
      {
        status: 400,
        description: `Validation failure (missing customerId)`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have permission to check eligibility for this customer and organisation`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/v2/risk/assessment/summary/organisation/:orgId',
    alias: 'getV2riskassessmentsummaryorganisationOrgId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'orgId',
        type: 'Path',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid(),
      },
      {
        name: 'assessedRiskLevel',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'assessmentStatus',
        type: 'Query',
        schema: z.enum(['NoRisksDetected', 'Open', 'Closed']).optional(),
      },
      {
        name: 'currentRiskLevel',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'customerId',
        type: 'Query',
        schema: z
          .string()
          .regex(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
          .uuid()
          .optional(),
      },
      {
        name: 'customerName',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'entityType',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'fromDateCreated',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'fromLastUpdated',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'isOverridden',
        type: 'Query',
        schema: z.boolean().optional(),
      },
      {
        name: 'lastIndex',
        type: 'Query',
        schema: z.number().int().optional().default(24),
      },
      {
        name: 'orderBy',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'orderDirection',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'presentedRiskLevel',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'startIndex',
        type: 'Query',
        schema: z.number().int().optional().default(0),
      },
      {
        name: 'toDateCreated',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'toLastUpdated',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: RiskAssessmentSummaryV2PageResponse,
    errors: [
      {
        status: 401,
        description: `User must be authenticated to access this endpoint.`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have permission to read assessments for this organisation.`,
        schema: z.void(),
      },
    ],
  },
]);

export const api = new Zodios(endpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
